// Track surface painter: draws the static "look" of a track (grass, asphalt, white edge lines,
// start/finish band, pit box) from its JSON geometry into a plain 2D canvas context.
//
// This file is the ONLY home of the painter. The game does not paint a track any more: what it shows
// is the picture this file produces, exported by the editor (see views/TrackEditorView.vue) — so the
// preview and the exported picture cannot disagree, because they are the same drawing.
//
// Two rules keep this file reusable, and they are the whole point of it:
//  - no host objects (no Phaser, no Vue): a plain 2D context in, pixels out.
//  - everything is derived from the pieces the track FILE carries, sampled by trackGeometry.js, so
//    the asphalt is always exactly the geometry the physics runs on: the game's server samples the
//    same curves with the same math (gpo2/src/server/game/trackGeometry.js) and tests the cars
//    against those.
//
// trackGeometry.js, trackShape.js, materials.js and TrackSurroundings.js are verbatim copies from the
// game (see `npm run sync:painter`): edit those in the game and re-sync, never here.
//
// Cost note: painting is done once per track load, into a canvas the size of the track times
// PAINT_SCALE (a big map can be 6400x6200; see paintScaleFor). Tiles are
// patterns repeated by the canvas itself, so the per-frame cost in game is a single textured quad,
// and painting cost is one stroke per path segment — no per-pixel loops over the whole image.
import { samplePiece, widthOf } from './trackGeometry.js';
import { MITER_LIMIT, chainRuns, isReversal, roadBoundary, unit, withoutBoundarySpikes, withoutDuplicates } from './trackShape.js';
import {
  GRASS_PALETTE, PAINT_SCALE, TERRAIN_TILE, TILE, VEGETATION_SPRITES, defaultCreateCanvas, hashString,
  makeGrassTile, mulberry32, paintGrass, paintPlants, repeatingPattern, scaleWithinCeilings,
  setTileWorldSize, terrainImageSrc,
} from './TrackSurroundings.js';

// Re-exported for the callers that read the ceilings while working with the painter (the editor exports a
// picture at the scale `scaleWithinCeilings` allows). The definition is in TrackSurroundings, with the rest
// of the ground: one home, this is a door to it.
export { PAINT_SCALE, scaleWithinCeilings } from './TrackSurroundings.js';
import { drawStand } from './standLayout.js';
import { DEFAULT_MATERIAL, materialLook, materialNameFor } from './materials.js';
import { terrainBlendOf, grassTextureOf, inPaintLayers, inPaintOrder, terrainOf } from './trackFormat.js';
import { drawFence } from './fenceLayout.js';
import {
  compareTyreDepth, drawTyreCell, rectSpine, tyreCircleStamps, tyreSet, tyreStampsForObstacle,
} from './tyreLayout.js';

export const PAINTER_VERSION = 68;

export const SURFACE_STYLES = ['gpo'];
const DEFAULT_SURFACE = SURFACE_STYLES[0];

// Colors of the generated surface. Deliberately a restrained illustrated palette: it sits next to the
// game's flat 2D cars/shadows, and a photorealistic base would make those look like stickers.
//
// Grass tones match the reference-derived illustrated asset and keep cars and objects easy to read.
// Loaded images retain their authored colours; these tones are also used for edge blades and fallback.
export const PALETTE = {
  ...GRASS_PALETTE,
  asphalt:      '#4a4b51',
  asphaltLight: '#5c5d64',
  asphaltDark:  '#3e3f44',
  line:         '#dededd',
  pitLine:      '#e3c14a',
  pitSlab:      '#585960',
  checkerA:     '#f0f0ec',
  checkerB:     '#1e1f24',
};

/**
 * The GROUND a terrain paints, one entry per terrain name (see the game's own simulation catalogue,
 * server/config/physicsConfig.js::TERRAINS — the names have to match). The tile comes from the terrain's
 * own texture when the host could load it and from the colours here when it could not, so a missing file
 * is a plainer track, never a broken one. `grass` has no entry: it IS the base surface, painted by
 * paintGrass from PALETTE, and a grass figure therefore changes nothing to look at.
 */
const TERRAIN_LOOKS = {
  grass_dirt:             { base: '#998265', light: '#aa9376', dark: '#806c52', grit: 2600 },
  fine_grass:             { base: '#687d51', light: '#74895d', dark: '#54683f', grit: 2600 },
  cobblestone:            { base: '#93918a', light: '#a4a299', dark: '#7d7b75', grit: 900, cells: true },
  cobblestone_grass_grid: { base: '#a09e93', light: '#aeac9f', dark: '#858777', grit: 1200, cells: true },
  sand:                   { base: '#b9a780', light: '#c9b993', dark: '#a2916d', grit: 3000 },
  sand2:                  { base: '#c4b697', light: '#d2c6aa', dark: '#afa182', grit: 3000 },
  rocky_sand:             { base: '#b3a283', light: '#c3b498', dark: '#9b8a70', grit: 2200 },
  fine_grass2:            { base: '#556226', light: '#697437', dark: '#415015', grit: 2600 },
  lawn:                   { base: '#5a6f25', light: '#667b31', dark: '#4e631a', grit: 2600 },
  dirt:                   { base: '#977e64', light: '#a2886e', dark: '#8b7258', grit: 2600 },
  dirt_grass:             { base: '#5f582f', light: '#736d42', dark: '#4a421b', grit: 2600 },
  red_dirt:               { base: '#9e6a3f', light: '#b48155', dark: '#89532a', grit: 2600 },
  forest_floor:           { base: '#53402e', light: '#6a5744', dark: '#3c2919', grit: 2600 },
  mud:                    { base: '#493222', light: '#654c3a', dark: '#2f190b', grit: 2600 },
  mud_grass:              { base: '#5a4f37', light: '#6c6048', dark: '#483d26', grit: 2600 },
  compact_sand:           { base: '#b39879', light: '#bfa384', dark: '#a78c6d', grit: 2600 },
  gravel:                 { base: '#635c50', light: '#928b7e', dark: '#373125', grit: 3200 },
  dense_paving:           { base: '#887453', light: '#978261', dark: '#726340', grit: 900, cells: true },
  paving_grass:           { base: '#9e9781', light: '#c1b8ae', dark: '#68673e', grit: 1200, cells: true },
  snow:                   { base: '#c6c6c6', light: '#cbcbcb', dark: '#c1c1c1', grit: 1800 },
  snow_grass:             { base: '#a1a597', light: '#cccfc5', dark: '#727764', grit: 2600 },
  snow_dirt:              { base: '#90867d', light: '#b6aea7', dark: '#6b5f54', grit: 2600 },
};

/**
 * A terrain's texture is always found by the same convention — one folder, the terrain's own name:
 * `textures/terrains/<terrain>.jpg`. The path is relative to the game's assets img root (the host
 * resolves `assets/img/<src>` in game and `/img/<src>` in the editor preview, exactly like materials.js).
 * A file that is not there (or fails to load) falls back to the look's colours.
 */
const TAU = Math.PI * 2;
const EDGE_LINE_WIDTH = 7; // white line marking the asphalt edge
const EDGE_INSET = 0;      // the line sits ON the asphalt's edge (style GPO, see paintUnion)
// Every piece of asphalt is drawn half a unit wider than its shape. Two pieces that merely touch, and
// two stretches of the same road (a width ramp is one stroke per piece pair), then share an edge: each
// covers the boundary pixel by about a half and what is left of the grass underneath shows through as
// a hairline â€” a green thread in the middle of the road exactly where nothing is wrong. Overlapping by
// half a unit closes it, and half a unit of extra asphalt around the whole track is invisible.
const ASPHALT_BLEED = 1;
const START_BAND_ROWS = 1;   // the line is one checker deep: a band, but a thin one
const START_BAND_CELL = 10;  // world units per checker along the line
const PIT_START_LINE_WIDTH = 3; // the pit lane crossing is a plain line, thinner than the band

// â”€â”€ deterministic randomness â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Both the tiles and the decoration scatter are seeded from the track id, so a given track always
// paints identically: the preview in the editor and the texture in game must not differ run to run.

// â”€â”€ procedural tiles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function resoledCanvas(createCanvas, scale) {
  if (!(scale > 1)) return createCanvas;
  return (w, h) => {
    const canvas = createCanvas(Math.ceil(w * scale), Math.ceil(h * scale));
    // Rounding up the bitmap but using the requested scale leaves a partly transparent last
    // row/column. Repeating that edge exposes the grass through asphalt and sand as a grid.
    canvas.getContext('2d').scale(canvas.width / w, canvas.height / h);
    setTileWorldSize(canvas, { width: w, height: h });
    return canvas;
  };
}

/** A rounded rectangle path (canvas roundRect is not everywhere), used by the cobblestone tile. */
function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * One terrain's seamless tile, from the texture when there is one and drawn from the look's colours
 * when there is not. Images use TERRAIN_TILE like the base grass; procedural fallbacks keep TILE
 * and their existing grain density (see paintTerrains).
 */
function makeTerrainTile(createCanvas, rng, look, image) {
  const size = image ? TERRAIN_TILE : TILE;
  const canvas = createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  if (image) {
    ctx.drawImage(image, 0, 0, size, size);
    return canvas;
  }
  ctx.fillStyle = look.base;
  ctx.fillRect(0, 0, TILE, TILE);
  if (look.cells) {
    const cell = 32;
    for (let y = 0; y < TILE; y += cell) {
      for (let x = 0; x < TILE; x += cell) {
        const ox = (rng() - 0.5) * 3, oy = (rng() - 0.5) * 3;
        ctx.fillStyle = rng() < 0.5 ? look.light : look.dark;
        ctx.globalAlpha = 0.45 + rng() * 0.4;
        roundRectPath(ctx, x + 2 + ox, y + 2 + oy, cell - 4, cell - 4, 7);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }
  for (let i = 0; i < look.grit; i++) {
    ctx.fillStyle = rng() < 0.5 ? look.dark : look.light;
    ctx.globalAlpha = 0.12 + rng() * 0.3;
    ctx.fillRect(rng() * TILE, rng() * TILE, 1 + rng() * 1.6, 1 + rng() * 1.6);
  }
  ctx.globalAlpha = 1;
  return canvas;
}

/**
 * The transition between two grounds. A terrain does not end on a drawn line: the ground thins out into
 * the one it borders. Blending with an alpha mask would need a canvas the size of the area — and one more
 * for every strip an export is painted in — so the fade is built from rings of the SAME pattern around the
 * polygon, each wider than the last and fainter: their cumulative alpha falls linearly with the distance
 * from the edge, and the alpha of ring k is 1/(k+1), which is what makes that sum exact. The pattern is
 * world-anchored, so a ring and the fill it grows out of are the same picture, with no seam; the rings
 * wander a little so the fade reads as a ragged rim rather than a halo.
 */
const TERRAIN_BLEND = 24;         // world units the ground fades over
const TERRAIN_BLEND_RINGS = 12;   // alpha steps over that distance
const TERRAIN_BLEND_JITTER = 4;   // world units a ring wanders off the polygon

function insideBounds(ring, bounds, pad) {
  if (!bounds) return true;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const { x, y } of ring) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return maxX + pad >= bounds.x && minX - pad <= bounds.x + bounds.width
    && maxY + pad >= bounds.y && minY - pad <= bounds.y + bounds.height;
}

function paintTerrainBlend(ctx, ring, rng, bounds) {
  if (!insideBounds(ring, bounds, TERRAIN_BLEND * 1.5)) return;
  const ringPath = () => {
    ctx.beginPath();
    for (let i = 0; i < ring.length; i++) {
      const x = ring[i].x + (rng() - 0.5) * TERRAIN_BLEND_JITTER;
      const y = ring[i].y + (rng() - 0.5) * TERRAIN_BLEND_JITTER;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
  };
  // The last stretch is the faintest one: it takes the outermost ring's alpha down to nothing.
  ctx.globalAlpha = 0.03;
  ctx.lineWidth = TERRAIN_BLEND * 2.6;
  ringPath();
  ctx.stroke();
  for (let k = 1; k <= TERRAIN_BLEND_RINGS; k++) {
    ctx.globalAlpha = 1 / (k + 1);
    ctx.lineWidth = (TERRAIN_BLEND * k / TERRAIN_BLEND_RINGS) * 2;
    ringPath();
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

/**
 * The terrain areas: the GROUND painted over the base surface, each filled with its terrain's own tile
 * (see TERRAIN_LOOKS). Painted right after the grass and BEFORE the asphalt, because terrain is the ground
 * the road sits on — a terrain drawn under a road area is covered by it, which is exactly how the
 * physics treats it too (see trackUtils.terrainAt: the road wins). The name is the area's own, resolved
 * (see terrainOf): a figure with no `terrain` in the file is grass, so it paints the grass picture over a
 * base painted with another one. Only a name with neither a texture on hand nor a look is skipped.
 */
function paintTerrains(ctx, track, createCanvas, seed, images = {}, bounds = null) {
  const areas = inPaintOrder(track?.terrainAreas);
  if (!areas.length) return;
  const patterns = new Map();
  for (const area of areas) {
    const name = terrainOf(area);
    const image = images[name];
    const look = TERRAIN_LOOKS[name];
    // Nothing to paint this terrain with: not a texture on hand AND no procedural fallback for the name
    // (a terrain this build doesn't know, with its file missing too).
    if (!image && !look) continue;
    const ring = closedRing((area.points ?? []).filter((p) => Number.isFinite(p?.x) && Number.isFinite(p?.y)));
    if (ring.length < 3) continue;
    let pattern = patterns.get(name);
    if (!pattern) {
      const tile = makeTerrainTile(createCanvas, mulberry32(seed ^ hashString(name)), look, image);
      pattern = repeatingPattern(ctx, tile);
      patterns.set(name, pattern);
    }
    ctx.save();
    ctx.fillStyle = pattern;
    ctx.strokeStyle = pattern;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(ring[0].x, ring[0].y);
    for (let i = 1; i < ring.length; i++) ctx.lineTo(ring[i].x, ring[i].y);
    ctx.closePath();
    ctx.fill();
    // One seed per area: two patches of the same ground don't break up identically.
    const anchor = ring[0];
    const areaSeed = seed ^ hashString(`${name}:${Math.round(anchor.x)}:${Math.round(anchor.y)}:${ring.length}`);
    if (terrainBlendOf(area)) paintTerrainBlend(ctx, ring, mulberry32(areaSeed), bounds);
    ctx.restore();
  }
}

function makeAsphaltTile(createCanvas, rng, p) {
  const canvas = createCanvas(TILE, TILE);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = p.asphalt;
  ctx.fillRect(0, 0, TILE, TILE);
  for (let i = 0; i < 5200; i++) {
    ctx.fillStyle = rng() < 0.5 ? p.asphaltDark : p.asphaltLight;
    ctx.globalAlpha = 0.1 + rng() * 0.26;
    ctx.fillRect(rng() * TILE, rng() * TILE, 1 + rng() * 1.6, 1 + rng() * 1.6);
  }
  ctx.globalAlpha = 1;
  return canvas;
}

// â”€â”€ geometry helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/** Closest point on a→b to (px, py), with the parameter along the segment. */
function closestOnSegment(a, b, px, py) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / len2)) : 0;
  const x = a.x + dx * t;
  const y = a.y + dy * t;
  return { x, y, t, dist: Math.hypot(px - x, py - y) };
}

/**
 * Closest approach of two segments: the crossing point when they intersect, otherwise an endpoint
 * projected on the other segment. The returned point is always on the FIRST segment (the road).
 */
function closestBetweenSegments(a, b, c, d) {
  const rx = b.x - a.x;
  const ry = b.y - a.y;
  const sx = d.x - c.x;
  const sy = d.y - c.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) > 1e-9) {
    const qx = c.x - a.x;
    const qy = c.y - a.y;
    const t = (qx * sy - qy * sx) / denom;
    const u = (qx * ry - qy * rx) / denom;
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return { x: a.x + rx * t, y: a.y + ry * t, t, dist: 0 };
  }
  let best = null;
  for (const p of [c, d]) {
    const hit = closestOnSegment(a, b, p.x, p.y);
    if (!best || hit.dist < best.dist) best = hit;
  }
  for (const [p, t] of [[a, 0], [b, 1]]) {
    const hit = closestOnSegment(c, d, p.x, p.y);
    if (!best || hit.dist < best.dist) best = { x: p.x, y: p.y, t, dist: hit.dist };
  }
  return best;
}

/**
 * Where the start line crosses ONE road piece: the point of its centreline closest to the checkpoint
 * segment, and the road's half width there. `null` when the line does not reach the asphalt.
 *
 * Every piece is tested rather than a single nearest path: the line crosses the main road AND the
 * pit lane, and a lone nearest-path lookup can land on the wrong one, taking the whole band off the
 * road with it.
 */
function startCrossing(piece, p1, p2, roadWidth) {
  let best = null;
  for (const run of piece.runs ?? []) {
    for (let i = 0; i < run.length - 1; i++) {
      const a = run[i];
      const b = run[i + 1];
      const hit = closestBetweenSegments(a, b, p1, p2);
      const wa = widthOf(a, roadWidth);
      const wb = widthOf(b, roadWidth);
      const halfWidth = (wa + (wb - wa) * hit.t) / 2;
      if (hit.dist > halfWidth + 4) continue;
      if (!best || hit.dist < best.dist) best = { x: hit.x, y: hit.y, halfWidth, dist: hit.dist };
    }
  }
  return best;
}

/** Segment crossing point, with the parameter along the SECOND segment (c→d). */
function segmentCrossing(a, b, c, d) {
  const rx = b.x - a.x;
  const ry = b.y - a.y;
  const sx = d.x - c.x;
  const sy = d.y - c.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const qx = c.x - a.x;
  const qy = c.y - a.y;
  const t = (qx * sy - qy * sx) / denom;
  const u = (qx * ry - qy * rx) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { x: a.x + rx * t, y: a.y + ry * t, u };
}

/**
 * Where the start line crosses a road AREA: the widest gap between its edges along the line, which is
 * the road the line passes through (a concave area can be entered twice). `null` when it misses it.
 */
function areaCrossing(piece, p1, p2) {
  const pts = piece.points ?? [];
  const hits = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const hit = segmentCrossing(pts[i], pts[i + 1], p1, p2);
    if (hit) hits.push(hit);
  }
  if (hits.length < 2) return null;
  hits.sort((a, b) => a.u - b.u);
  let best = null;
  for (let i = 1; i < hits.length; i++) {
    const length = Math.hypot(hits[i].x - hits[i - 1].x, hits[i].y - hits[i - 1].y);
    if (!best || length > best.length) {
      best = { x: (hits[i].x + hits[i - 1].x) / 2, y: (hits[i].y + hits[i - 1].y) / 2, length };
    }
  }
  return best;
}

/**
 * The outside of a bend, at an interior point: the piece of the disc of radius `r` that neither the
 * incoming nor the outgoing length of road covers â€” i.e. past the incoming piece's end cut and before
 * the outgoing one's start cut.
 *
 * Painting the WHOLE disc (as this used to) bulges the asphalt past that corner: invisible in the
 * middle of a road, but a disc whose centre is closer to the end of the run than its radius sticks out
 * beyond the end cap by up to a radius â€” and it is exactly that bulge that then covers a neighbouring
 * road's edge line (hot_road's road area by the pit box: a disc 40 units from the end of a 180-wide
 * road erased the area's whole bottom edge line).
 */
function fillRoundJoin(ctx, previous, point, next, r) {
  const d1 = unit(point.x - previous.x, point.y - previous.y);
  const d2 = unit(next.x - point.x, next.y - point.y);
  if (!d1 || !d2) return;
  const cross = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(cross) < 1e-9) return; // straight on (or doubled back): the bands already meet

  const n1 = { x: -d1.y, y: d1.x };
  const n2 = { x: -d2.y, y: d2.x };
  const pastIncoming = (q) => (q.x - point.x) * d1.x + (q.y - point.y) * d1.y;
  const beforeOutgoing = (q) => (q.x - point.x) * d2.x + (q.y - point.y) * d2.y;
  let from = null;
  let to = null;
  for (const side of [1, -1]) {
    const onFirstCut = { x: point.x + n1.x * r * side, y: point.y + n1.y * r * side };
    const onSecondCut = { x: point.x + n2.x * r * side, y: point.y + n2.y * r * side };
    if (beforeOutgoing(onFirstCut) < 0) from = onFirstCut;
    if (pastIncoming(onSecondCut) > 0) to = onSecondCut;
  }
  if (!from || !to) return;

  const start = Math.atan2(from.y - point.y, from.x - point.x);
  const end = Math.atan2(to.y - point.y, to.x - point.x);
  const inside = (angle) =>
    pastIncoming({ x: point.x + Math.cos(angle) * r, y: point.y + Math.sin(angle) * r }) > 0
    && beforeOutgoing({ x: point.x + Math.cos(angle) * r, y: point.y + Math.sin(angle) * r }) < 0;
  let sweep = end - start;
  while (sweep > Math.PI) sweep -= TAU;
  while (sweep < -Math.PI) sweep += TAU;
  if (!inside(start + sweep / 2)) sweep += sweep > 0 ? -TAU : TAU;
  if (!inside(start + sweep / 2)) return;

  ctx.beginPath();
  ctx.moveTo(point.x, point.y);
  ctx.arc(point.x, point.y, r, start, start + sweep);
  ctx.closePath();
  ctx.fill();
}

// â”€â”€ layers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function paintRoadSurface(ctx, runs, asphaltPattern) {
  for (const run of runs) {
    ctx.strokeStyle = asphaltPattern;
    ctx.lineCap = 'butt'; // the road stops where its path stops: nothing bulges past the last point
    for (let i = 0; i < run.length - 1; i++) {
      const w = (run[i].width + run[i + 1].width) / 2 + ASPHALT_BLEED;
      if (!(w > 0)) continue;
      // An INTERIOR end of a stretch runs a hair into the next one, so the two overlap instead of
      // butting: their shared edge is exactly where the grass would show through (the sideways version
      // of the same thing is ASPHALT_BLEED on the width). The ends of the run stay exact â€” there the
      // road really does stop, and the half unit would stick out.
      const dir = unit(run[i + 1].x - run[i].x, run[i + 1].y - run[i].y);
      const over = ASPHALT_BLEED / 2;
      const from = dir && i > 0 ? { x: run[i].x - dir.x * over, y: run[i].y - dir.y * over } : run[i];
      const to = dir && i < run.length - 2
        ? { x: run[i + 1].x + dir.x * over, y: run[i + 1].y + dir.y * over }
        : run[i + 1];
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
    }

    // Round joins: the outside of a bend is filled by the piece of the disc at the corner that neither
    // the incoming nor the outgoing length of road reaches.
    ctx.fillStyle = asphaltPattern;
    for (let i = 1; i < run.length - 1; i++) {
      const r = run[i].width / 2;
      if (!(r > 0) || isReversal(run[i - 1], run[i], run[i + 1])) continue;
      fillRoundJoin(ctx, run[i - 1], run[i], run[i + 1], r + ASPHALT_BLEED / 2);
    }
  }
}

// â”€â”€ asphalt pieces, and the lines along their edges â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// The road is a set of PIECES: free areas (a closed polygon, the shape the editor draws when the
// road is not a simple band) and corridors (trackPath and its branches). Pieces overlap on purpose â€”
// an area is exactly how you widen or join what the corridors can't express â€” and where they do, the
// asphalt is continuous. So a piece's edge line must NOT be painted where another piece's asphalt
// covers it: it would cut straight across the road. The covered stretches are dropped while the line
// is built (see pieceLines and trimRing).

/** A corridor piece: the runs to paint, at their own per-point widths. */
function corridorPiece(runs, fallbackWidth, id, pit, surface) {
  return { kind: 'path', id, pit, surface, runs, fallbackWidth };
}

function paintPieceAsphalt(ctx, piece, surfaces) {
  const asphaltPattern = (surfaces.get(piece.surface) ?? surfaces.get(DEFAULT_SURFACE)).asphalt;
  if (piece.kind === 'area') {
    ctx.fillStyle = asphaltPattern;
    ctx.beginPath();
    ctx.moveTo(piece.points[0].x, piece.points[0].y);
    for (let i = 1; i < piece.points.length; i++) ctx.lineTo(piece.points[i].x, piece.points[i].y);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = asphaltPattern;
    ctx.lineWidth = ASPHALT_BLEED;
    ctx.stroke();
    return;
  }
  paintRoadSurface(ctx, piece.runs, asphaltPattern);
}

/**
 * Inward offset of a closed polygon by `d`: every vertex moved along its angle bisector, cut off at
 * MITER_LIMIT exactly like the corridor boundaries. An area's line is the inset of its own perimeter,
 * the same relationship the corridors' lines have with theirs.
 *
 * A vertex of the inset only exists where the perimeter is at least `d` away from itself. Where a
 * corner is tighter than that (the pit-box bend of hot_road's road area: a 13-unit rounding hosting a
 * 14.5-unit inset) the offsets of the vertices around it cross each other and fold the line over
 * itself â€” a bowtie painted in the middle of the asphalt. Those vertices are dropped and the two
 * edges AROUND the fold meet instead, which is the corner the line really has there.
 */
function insetPolygon(points, d) {
  // An area's sampled perimeter repeats its first point at the end â€” that is how a loop is written,
  // and what the game reads â€” but for the offset it is the SAME vertex twice: it has no direction,
  // and leaving it in produces a chord straight across the shape.
  const ring = closedRing(points);
  const count = ring.length;
  if (count < 3) return [];
  let area = 0;
  for (let i = 0; i < count; i++) {
    const a = ring[i], b = ring[(i + 1) % count];
    area += a.x * b.y - b.x * a.y;
  }
  const flip = area > 0 ? 1 : -1; // screen coords (y down): positive shoelace area = clockwise
  const inward = (u) => ({ x: -u.y * flip, y: u.x * flip });

  const edgeDir = (i) => unit(ring[(i + 1) % count].x - ring[i].x, ring[(i + 1) % count].y - ring[i].y);
  // The offset line of one edge: a point on it and its direction, so two of them can be intersected.
  const offsetLine = (i) => {
    const dir = edgeDir(i);
    if (!dir) return null;
    const n = inward(dir);
    return { dir, at: { x: ring[i].x + n.x * d, y: ring[i].y + n.y * d } };
  };
  const lengthOf = (i) => Math.hypot(ring[(i + 1) % count].x - ring[i].x, ring[(i + 1) % count].y - ring[i].y);

  // Distance from `point` to an edge, used only to look for OTHER edges close enough to be in conflict.
  const distanceToEdge = (point, i) => {
    const a = ring[i], b = ring[(i + 1) % count];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / len2)) : 0;
    return Math.hypot(point.x - (a.x + dx * t), point.y - (a.y + dy * t));
  };

  const WINDOW = 8; // a fold is always local: the corner is a handful of vertices wide
  const raw = [];      // one candidate per ring vertex, aligned with the ring's indices
  const validAt = [];
  for (let i = 0; i < count; i++) {
    const previous = ring[(i - 1 + count) % count];
    const point = ring[i];
    const next = ring[(i + 1) % count];
    const inDir = unit(point.x - previous.x, point.y - previous.y);
    const outDir = unit(next.x - point.x, next.y - point.y);
    if (!inDir || !outDir) { raw.push(null); validAt.push(false); continue; }
    const n1 = inward(inDir), n2 = inward(outDir);
    const p1 = { x: point.x + n1.x * d, y: point.y + n1.y * d };
    const p2 = { x: point.x + n2.x * d, y: point.y + n2.y * d };
    const cross = inDir.x * outDir.y - inDir.y * outDir.x;
    let candidate = p2;
    if (Math.abs(cross) > 1e-9) {
      const t = ((p2.x - p1.x) * outDir.y - (p2.y - p1.y) * outDir.x) / cross;
      candidate = { x: p1.x + inDir.x * t, y: p1.y + inDir.y * t };
      if (Math.hypot(candidate.x - point.x, candidate.y - point.y) > d * MITER_LIMIT) {
        const bx = n1.x + n2.x, by = n1.y + n2.y;
        const bl = Math.hypot(bx, by);
        candidate = bl > 1e-6 ? { x: point.x + (bx / bl) * d, y: point.y + (by / bl) * d } : p2;
      }
    }
    let valid = true;
    for (let k = 1; k <= WINDOW && valid; k++) {
      for (const j of [i - k, i + k]) {
        const edge = ((j % count) + count) % count;
        if (edge === i) continue;
        // Edges much shorter than the inset belong to the same corner: they can't pull the line back.
        if (lengthOf(edge) < d * 0.5) continue;
        if (distanceToEdge(candidate, edge) < d - 1) { valid = false; break; }
      }
    }
    raw.push(candidate);
    validAt.push(valid);
  }
  const kept = [];
  for (let i = 0; i < raw.length; i++) if (validAt[i]) kept.push({ edge: i, point: raw[i] });
  if (kept.length < 3) return [];

  // Wherever a run was dropped, the two edges around it meet: their offset lines cross at the corner
  // that replaces everything the fold swallowed.
  const result = [];
  for (let k = 0; k < kept.length; k++) {
    const a = kept[k];
    const b = kept[(k + 1) % kept.length];
    result.push(a.point);
    if (((b.edge - a.edge) % count + count) % count === 1) continue;
    // The corner is where the line leaving the last kept vertex meets the line arriving at the next
    // one. Those two lines can be parallel (the fold swallowed a run that ends on a stub edge): the
    // line just after the run meets it instead, which is the edge the fold was hiding.
    const before = offsetLine(a.edge);
    const corner = before && [
      offsetLine((b.edge - 1 + count) % count),
      offsetLine(b.edge),
      offsetLine((b.edge + 1) % count),
    ].map((line) => line && linesCross(before, line)).find((point) => point);
    if (!corner) continue;
    if (Math.hypot(corner.x - a.point.x, corner.y - a.point.y) > d * 6) continue; // a wild spike, not a corner
    result.push(corner);
  }

  // The sampled perimeter carries vertices 1-3px apart wherever a curve was drawn: offset by the
  // line's own inset they throw it a few units off, so the doubling-back cleanup the corridor
  // boundary gets runs here too.
  return withoutBoundarySpikes(result);
}

/** Where two offset lines cross, or null when they are too close to parallel to have a corner. */
function linesCross(a, b) {
  const cross = a.dir.x * b.dir.y - a.dir.y * b.dir.x;
  if (Math.abs(cross) < 0.2) return null;
  const t = ((b.at.x - a.at.x) * b.dir.y - (b.at.y - a.at.y) * b.dir.x) / cross;
  return { x: a.at.x + a.dir.x * t, y: a.at.y + a.dir.y * t };
}

function pieceCovers(piece, x, y, pad) {
  if (piece.kind === 'area') {
    return pointInRing(piece.points, x, y) || ringDistance(piece.points, x, y) <= pad;
  }
  for (const run of piece.runs) {
    for (let i = 0; i < run.length - 1; i++) {
      const a = run[i], b = run[i + 1];
      const dx = b.x - a.x, dy = b.y - a.y;
      const length = Math.hypot(dx, dy);
      if (!(length > 0)) continue;
      // Distance from the point to the road as a SHAPE (a band with flat ends, not a capsule): the
      // sideways gap and the overshoot past a cap combine, so a point level with a cap is on the road
      // while one behind it is off â€” the difference between the two ends of the same shared edge.
      const t = ((x - a.x) * dx + (y - a.y) * dy) / (length * length);
      const clamped = Math.max(0, Math.min(1, t));
      const half = (a.width + (b.width - a.width) * clamped) / 2;
      const sideways = Math.hypot(x - (a.x + dx * clamped), y - (a.y + dy * clamped)) - half;
      const overshoot = (t < 0 ? -t : t > 1 ? t - 1 : 0) * length;
      if (Math.hypot(Math.max(0, sideways), overshoot) <= pad) return true;
    }
  }
  return false;
}

function pointInRing(points, x, y) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i], b = points[j];
    if ((a.y > y) !== (b.y > y) && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function distanceToSegment(x, y, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / len2)) : 0;
  return Math.hypot(x - (a.x + dx * t), y - (a.y + dy * t));
}

function ringDistance(points, x, y) {
  let best = Infinity;
  for (let i = 0; i < points.length; i++) {
    best = Math.min(best, distanceToSegment(x, y, points[i], points[(i + 1) % points.length]));
  }
  return best;
}

/**
 * The perimeter of an area cut where another piece's asphalt continues it: the stretches that are
 * really on the union's boundary, as open polylines (or the whole ring, when nothing covers it).
 * Long edges are walked so a partly covered one keeps the part that isn't.
 */
function trimRing(ring, covered) {
  const count = ring.length;
  const coveredAt = ring.map((point) => covered(point.x, point.y));
  if (!coveredAt.some(Boolean)) return [{ points: ring, closed: true }];

  // Start from a covered point, so the stretches that are kept never wrap around the seam.
  const start = coveredAt.indexOf(true);
  const runs = [];
  let current = [];
  const flush = () => {
    if (current.length >= 2) runs.push({ points: current, closed: false });
    current = [];
  };
  const push = (point) => {
    const last = current[current.length - 1];
    if (last && Math.hypot(last.x - point.x, last.y - point.y) < 0.01) return;
    current.push(point);
  };

  for (let k = 0; k < count; k++) {
    const from = ring[(start + k) % count];
    const to = ring[(start + k + 1) % count];
    for (const piece of edgeCoverage(from, to, covered) ?? []) {
      if (!piece) { flush(); continue; }
      for (const point of piece) push(point);
    }
  }
  flush();
  return runs;
}

/**
 * How far a kept stretch of the perimeter runs INTO the part the neighbouring piece's asphalt takes
 * over. The two lines have to overlap: stopping one exactly where the other starts leaves a hairline a
 * fraction of a unit wide that reads as a break â€” the neighbour's line does not begin at the cut, it
 * begins where ITS own inset is, a few units further along.
 */
const LINE_JOIN_OVERLAP = EDGE_LINE_WIDTH;

/**
 * The part of an edge (from -> to) that is not covered, as an array of runs (null = a covered gap),
 * with the transition points found by bisection so the line starts exactly where the other piece's
 * asphalt begins â€” minus LINE_JOIN_OVERLAP, so the two lines overlap instead of leaving a hairline.
 */
function edgeCoverage(from, to, covered) {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  if (length < 0.01) return null;
  const room = Math.min(0.5, LINE_JOIN_OVERLAP / length);
  const steps = Math.max(1, Math.ceil(length / 4));
  const at = (t) => ({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t });
  const isCovered = (t) => covered(at(t).x, at(t).y);
  const runs = [];
  let current = [];
  let previous = 0;
  if (!isCovered(0)) current.push(from);
  for (let s = 1; s <= steps; s++) {
    const t = s / steps;
    const here = isCovered(t);
    if (here !== isCovered(previous)) {
      // Refine: the crossing is between the previous sample and this one.
      let low = previous, high = t;
      for (let k = 0; k < 12; k++) {
        const mid = (low + high) / 2;
        if (isCovered(mid) === here) high = mid; else low = mid;
      }
      const crossing = (low + high) / 2;
      if (here) {
        current.push(at(Math.min(1, crossing + room)));
        runs.push(current, null);
        current = [];
      } else {
        current.push(at(Math.max(0, crossing - room)));
      }
    }
    if (!here) current.push(at(t));
    previous = t;
  }
  if (current.length) runs.push(current);
  return runs.length ? runs : null;
}

/**
 * Inward offset of an OPEN polyline by `d` (the half of an area's boundary that a neighbour doesn't
 * continue): the same vertex math as the closed ring, minus the ring's own fold handling.
 */
function insetOpenPolyline(points, d, flip) {
  const inward = (u) => ({ x: -u.y * flip, y: u.x * flip });
  const out = [];
  const last = points.length - 1;
  for (let i = 0; i <= last; i++) {
    const previous = points[Math.max(0, i - 1)];
    const point = points[i];
    const next = points[Math.min(last, i + 1)];
    const inDir = unit(point.x - previous.x, point.y - previous.y) || unit(next.x - point.x, next.y - point.y);
    const outDir = unit(next.x - point.x, next.y - point.y) || inDir;
    if (!inDir || !outDir) continue;
    const n1 = inward(inDir), n2 = inward(outDir);
    const p1 = { x: point.x + n1.x * d, y: point.y + n1.y * d };
    const p2 = { x: point.x + n2.x * d, y: point.y + n2.y * d };
    const cross = inDir.x * outDir.y - inDir.y * outDir.x;
    let vertex = p2;
    if (Math.abs(cross) > 1e-9) {
      const t = ((p2.x - p1.x) * outDir.y - (p2.y - p1.y) * outDir.x) / cross;
      vertex = { x: p1.x + inDir.x * t, y: p1.y + inDir.y * t };
      if (Math.hypot(vertex.x - point.x, vertex.y - point.y) > d * MITER_LIMIT) {
        const bx = n1.x + n2.x, by = n1.y + n2.y;
        const bl = Math.hypot(bx, by);
        vertex = bl > 1e-6 ? { x: point.x + (bx / bl) * d, y: point.y + (by / bl) * d } : p2;
      }
    }
    out.push(vertex);
  }
  return withoutBoundarySpikes(out);
}

/**
 * The edge line of one piece (white, or yellow for a pit lane), inset from the asphalt edge.
 *
 * An area's line is the inset of the part of its perimeter that is on the UNION's boundary: the
 * stretches another piece's asphalt continues are dropped BEFORE offsetting, so a piece whose edge is
 * continued by the next one stops exactly where that one's line starts â€” the offset of a shared end
 * point is the same point for both, so the two lines meet instead of stopping a line-inset apart
 * (hot_road's road area on a corridor: a 15-unit stub, and the area's own line running straight
 * across the middle of the joined asphalt).
 */
function pieceLines(piece, covered) {
  const shrink = EDGE_INSET + EDGE_LINE_WIDTH / 2;
  const strokes = [];
  if (piece.kind === 'area') {
    const ring = closedRing(piece.points);
    if (ring.length < 3) return strokes;
    let winding = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i], b = ring[(i + 1) % ring.length];
      winding += a.x * b.y - b.x * a.y;
    }
    const flip = winding > 0 ? 1 : -1; // screen coords (y down): positive shoelace area = clockwise
    for (const run of trimRing(ring, covered)) {
      const line = run.closed ? insetPolygon(run.points, shrink) : insetOpenPolyline(run.points, shrink, flip);
      if (line.length >= 2) strokes.push({ points: line, closed: run.closed, pit: piece.pit === true });
    }
    return strokes;
  }

  for (const run of piece.runs) {
    for (const side of [1, -1]) {
      const line = roadBoundary(run, side, piece.fallbackWidth, shrink);
      // A corridor's line is cut where another piece's asphalt takes over, with the same rule an area's
      // ring is cut by (see trimRing). Cutting it here rather than letting the erasing pass shave it
      // means the end is found by bisection and not by the asphalt's antialiased edge: the neighbour
      // can begin a unit or two off, and the shaved end then shows as a notch with a blob of a cap in
      // the middle of the road.
      for (const stretch of uncoveredRuns(line, covered)) {
        strokes.push({ points: stretch, closed: false, pit: piece.pit === true });
      }
    }
  }
  return strokes;
}

/**
 * The stretches of a boundary polyline no other piece's asphalt covers, as open runs. The `null`
 * entries a boundary can already carry (a road too narrow for a line there) break a run as well.
 */
function uncoveredRuns(points, covered) {
  const runs = [];
  let current = [];
  const flush = () => {
    if (current.length >= 2) runs.push(current);
    current = [];
  };
  const push = (point) => {
    const last = current[current.length - 1];
    if (!last || Math.hypot(last.x - point.x, last.y - point.y) > 0.01) current.push(point);
  };

  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i];
    const to = points[i + 1];
    if (!from || !to) { flush(); continue; }
    for (const piece of edgeCoverage(from, to, covered) ?? []) {
      if (!piece) flush();
      else for (const point of piece) push(point);
    }
  }
  flush();
  return runs;
}

/**
 * Two ends of the line this close to each other are the same line interrupted by a piece boundary; a
 * real dead end has no other end anywhere near it (see joinLineEnds).
 */
const LINE_WELD_GAP = EDGE_LINE_WIDTH * 1.6;

/** Where the two ends would meet if each line carried on straight, or null if that is too far off. */
function lineEndCorner(a, aIn, b, bIn) {
  const dA = unit(a.x - aIn.x, a.y - aIn.y);
  const dB = unit(b.x - bIn.x, b.y - bIn.y);
  if (!dA || !dB) return null;
  const cross = dA.x * dB.y - dA.y * dB.x;
  if (Math.abs(cross) < 0.15) return null; // nearly parallel (or doubled back): no corner to make
  const t = ((b.x - a.x) * dB.y - (b.y - a.y) * dB.x) / cross;
  const corner = { x: a.x + dA.x * t, y: a.y + dA.y * t };
  if (Math.hypot(corner.x - a.x, corner.y - a.y) > LINE_WELD_GAP) return null;
  if (Math.hypot(corner.x - b.x, corner.y - b.y) > LINE_WELD_GAP) return null;
  return corner;
}

/** An area's sampled perimeter without the repeated closing point (see insetPolygon). */
function closedRing(points) {
  return points.length > 1
    && Math.hypot(points[0].x - points[points.length - 1].x, points[0].y - points[points.length - 1].y) < 0.01
    ? points.slice(0, -1)
    : points;
}

/**
 * Joins the ends of the line: two strokes stopping within a line's width of each other are one line
 * interrupted by a piece boundary, not two lines.
 *
 * The two ends are MOVED onto a single common point â€” the crossing of their directions where they meet
 * at an angle (a miter), their midpoint where they are nearly parallel â€” and no extra segment is drawn
 * between them. A bridge would leave the ends where they were, and at a junction the two insets differ
 * by a unit or two (that is what two clicks in the editor produce): what showed on screen was the step
 * between the two strokes plus the bridge hanging off the side of the line as a hook (hot_road's straight
 * where areas 6 and 7 meet, 2026-09-19).
 *
 * One join per end, the closest first: at a junction several ends sit within a line's width of each
 * other, and moving every pair onto one point pulled unrelated lines together.
 */
function joinLineEnds(strokes) {
  const ends = [];
  for (const stroke of strokes) {
    if (stroke.closed || stroke.points.length < 2) continue;
    const last = stroke.points.length - 1;
    ends.push({ stroke, at: 0, point: stroke.points[0], inside: stroke.points[1] });
    ends.push({ stroke, at: last, point: stroke.points[last], inside: stroke.points[last - 1] });
  }
  const pairs = [];
  for (let i = 0; i < ends.length; i++) {
    for (let j = i + 1; j < ends.length; j++) {
      if (ends[i].stroke === ends[j].stroke) continue; // one stroke's own two ends are not a join
      if (ends[i].stroke.pit !== ends[j].stroke.pit) continue;
      const gap = Math.hypot(ends[i].point.x - ends[j].point.x, ends[i].point.y - ends[j].point.y);
      if (gap <= LINE_WELD_GAP) pairs.push({ i, j, gap });
    }
  }
  pairs.sort((first, second) => first.gap - second.gap);
  const joined = new Set();
  for (const { i, j } of pairs) {
    if (joined.has(i) || joined.has(j)) continue;
    joined.add(i);
    joined.add(j);
    const a = ends[i];
    const b = ends[j];
    const joint = lineEndCorner(a.point, a.inside, b.point, b.inside)
      ?? { x: (a.point.x + b.point.x) / 2, y: (a.point.y + b.point.y) / 2 };
    a.stroke.points[a.at] = joint;
    b.stroke.points[b.at] = joint;
  }
  for (const stroke of strokes) dropCoincidentEnds(stroke.points, stroke.closed);
}

/** Removes the points an inset end can leave on top of each other once it has been moved (see
 *  joinLineEnds): a zero-length piece of polyline draws as nothing but breaks the round join. */
function dropCoincidentEnds(points, closed) {
  const same = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) < 0.5;
  while (points.length > 2 && same(points[0], points[1])) points.shift();
  while (points.length > 2 && same(points[points.length - 1], points[points.length - 2])) points.pop();
  if (closed && points.length > 2 && same(points[0], points[points.length - 1])) points.pop();
}

/**
 * Paints every piece's edge line over the asphalt, minus the parts another piece's asphalt covers:
 * where two pieces overlap the road is continuous, and a line running across it would read as a step.
 *
 * The covered stretches are dropped before the line is offset (see pieceLines), which is what makes the
 * two lines meet at a junction; the ends left over by those cuts are then joined onto each other
 * (joinLineEnds) before anything is drawn. An erasing pass on a scratch canvas used to shave them
 * instead, but it cuts with the asphalt's own antialiased edge: wherever the neighbour begins a unit or
 * two off â€” the common case, since the pieces come from the editor's clicks â€” it left a thin, pale stub
 * and a notch in the middle of the line (hot_road, 2026-09-19).
 */

// â”€â”€ the union's outline  â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Everything drawn ON the asphalt's boundary â€” the line, the packed earth, the grass â€” is drawn on the
// outline of the asphalt AS ONE REGION, never piece by piece. Per piece the question was "is someone
// else's asphalt within a unit of my edge?", and that question has no answer: the two edges of a seam
// differ by fractions of a unit, so a node nudged by two units flips the verdict (hot_road 2026-09-20:
// the pit lane's line appeared and disappeared) and when it guesses wrong the decoration lands INSIDE
// the asphalt (the apron's earth band painted across the main straight). Here the question is asked
// from the other side and cannot be ambiguous: a boundary point is on the outline when the world a
// couple of units OUTSIDE it is not asphalt â€” which is also, with no special case at all, what makes a
// crossing clean (the road that passes covers the other's edge, so that stretch is not on the outline)
// and what settles a T junction and a road running alongside.

const OUTLINE_PROBE = [2.5, 6]; // how far outside a boundary point we look for another piece's asphalt
const OUTLINE_STEP = 4;         // world units between the boundary samples the test is run on
const CHAIN_TOL = 4;            // ends nearer than this are the same outline, not two

/**
 * A piece's own boundary, as open polylines, each with the sign "k" that turns its direction into the
 * OUTWARD normal (see offsetChain). An area's ring is closed back on itself, so it comes back as one
 * polyline whose last point is the first again.
 */
function pieceBoundaries(piece) {
  const out = [];
  if (piece.kind === 'area') {
    const ring = closedRing(piece.points);
    if (ring.length < 3) return out;
    let winding = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i], b = ring[(i + 1) % ring.length];
      winding += a.x * b.y - b.x * a.y;
    }
    out.push({ points: [...ring, { ...ring[0] }], k: winding > 0 ? -1 : 1 });
    return out;
  }
  for (const run of piece.runs) {
    for (const side of [1, -1]) {
      const edge = roadBoundary(run, side, piece.fallbackWidth, 0, { mitre: piece.mitre === true });
      let part = [];
      for (const point of edge) {
        if (!point) {
          if (part.length >= 2) out.push({ points: part, k: side });
          part = [];
          continue;
        }
        part.push({ x: point.x, y: point.y });
      }
      if (part.length >= 2) out.push({ points: part, k: side });
    }
  }
  return out;
}

/** The polyline with a point every "step" units: a straight edge is two points, and the test has to
 *  look in between them (a neighbour's asphalt can cover the middle of my straight edge). */
function densify(points, step) {
  const out = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const parts = Math.max(1, Math.ceil(length / step));
    for (let s = 0; s < parts; s++) {
      const t = s / parts;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  out.push(points[points.length - 1]);
  return out;
}

/** True when the world just outside this boundary point is not asphalt: the point is on the union's
 *  outline, i.e. the one place a line, a kerb or a band of earth can be drawn. */
function onOutline(point, dir, k, others) {
  if (!dir) return false;
  const nx = -dir.y * k, ny = dir.x * k;
  for (const gap of OUTLINE_PROBE) {
    const x = point.x + nx * gap, y = point.y + ny * gap;
    for (const other of others) if (pieceCovers(other, x, y, 1)) return false;
  }
  return true;
}

/** The outline of the asphalt as ONE region: every piece's boundary stretches whose outside is grass,
 *  chained into continuous polylines. */
function unionOutline(pieces) {
  const runs = [];
  for (const piece of pieces) {
    const others = pieces.filter((q) => q !== piece);
    for (const boundary of pieceBoundaries(piece)) {
      const points = densify(boundary.points, OUTLINE_STEP);
      let current = [];
      const flush = () => {
        if (current.length >= 2) runs.push({ points: current, k: boundary.k, pit: piece.pit === true, surface: piece.surface });
        current = [];
      };
      for (let i = 0; i < points.length; i++) {
        const previous = points[Math.max(0, i - 1)];
        const next = points[Math.min(points.length - 1, i + 1)];
        const dir = unit(next.x - previous.x, next.y - previous.y);
        if (onOutline(points[i], dir, boundary.k, others)) current.push(points[i]);
        else flush();
      }
      flush();
    }
  }
  return chainOutline(runs);
}

/**
 * Joins the runs whose ends meet into one polyline: the seam between two pieces IS one outline, and
 * drawing the two halves separately is what used to need welds, miters and tolerance windows.
 *
 * Only runs that agree on the outward side chain (a run appended backwards flips its own sign): an
 * outward that flips means the two runs are two different outlines running back to back, and joining
 * those would draw a line straight across the asphalt. Same for the surface: two of them meeting on
 * the asphalt is a boundary between two looks, which no single stroke can represent.
 */
function chainOutline(runs) {
  const chains = [];
  const used = new Array(runs.length).fill(false);
  const near = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) <= CHAIN_TOL;
  for (let i = 0; i < runs.length; i++) {
    if (used[i]) continue;
    used[i] = true;
    const chain = { points: [...runs[i].points], k: runs[i].k, pit: runs[i].pit, surface: runs[i].surface };
    let grew = true;
    while (grew) {
      grew = false;
      for (let j = 0; j < runs.length; j++) {
        if (used[j] || runs[j].pit !== chain.pit || runs[j].surface !== chain.surface) continue;
        const run = runs[j];
        const head = chain.points[0];
        const tail = chain.points[chain.points.length - 1];
        const first = run.points[0];
        const last = run.points[run.points.length - 1];
        if (run.k === chain.k && near(tail, first)) chain.points.push(...run.points.slice(1));
        else if (-run.k === chain.k && near(tail, last)) chain.points.push(...run.points.slice(0, -1).reverse());
        else if (run.k === chain.k && near(head, last)) chain.points.unshift(...run.points.slice(0, -1));
        else if (-run.k === chain.k && near(head, first)) chain.points.unshift(...run.points.slice(1).reverse());
        else continue;
        used[j] = true;
        grew = true;
      }
    }
    chains.push(chain);
  }
  return chains;
}

/** The outline moved "d" along its outward normal (negative = inward). Per point, by its own
 *  direction: these are soft bands, so they need no miter. */
function offsetChain(points, k, d) {
  const out = [];
  for (let i = 0; i < points.length; i++) {
    const previous = points[Math.max(0, i - 1)];
    const next = points[Math.min(points.length - 1, i + 1)];
    const dir = unit(next.x - previous.x, next.y - previous.y) ?? { x: 1, y: 0 };
    out.push({ x: points[i].x - dir.y * d * k, y: points[i].y + dir.x * d * k });
  }
  return out;
}

/** Everything that lives on the asphalt's boundary, once per outline: the line along it, the packed
 *  earth outside it, and the grass over that. Each outline is decorated with the textures of the
 *  surface style of the piece it came from, which is the only thing a piece's `surface` decides. */
function paintUnion(ctx, pieces, rng, p, surfaces) {
  const chains = unionOutline(pieces);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  // A phase per outline (from where it starts): the tiles repeat every LINE_TILE units, and every
  // outline starting at phase zero would put the same worn patch at the same distance along each edge.
  const phase = (chain) => chain.points?.find(Boolean) ?? { x: 0, y: 0 };
  for (const chain of chains) {
    const tex = surfaces.get(chain.surface) ?? surfaces.get(DEFAULT_SURFACE);
    const at = phase(chain);
    const tile = chain.pit ? tex.pitLineTile : tex.lineTile;
    ctx.strokeStyle = (tile && patternAt(ctx, tile, at.x, at.y))
      ?? (chain.pit ? tex.pitLine : tex.line)
      ?? (chain.pit ? p.pitLine : p.line);
    ctx.lineWidth = EDGE_LINE_WIDTH;
    strokeRun(ctx, chain.points);
  }
  for (const chain of chains) {
    const tex = surfaces.get(chain.surface) ?? surfaces.get(DEFAULT_SURFACE);
    const at = phase(chain);
    paintRunOff(ctx, chain, tex.sandTile ? patternAt(ctx, tex.sandTile, at.x, at.y) : tex.sand);
  }
  for (const chain of chains) scatterEdgeWear(ctx, chain.points, chain.k, rng, p);
}


// The surface is the geometry, and geometry has no grain: the asphalt/grass boundary came out as a
// razor-sharp cut and the white line as a perfect vector stroke. A real track's edge is a fringe â€”
// grass biting into the tarmac, paint eaten away, a little gravel spilled on the asphalt. All of it is
// DECORATION: it changes no geometry, so nothing under it (physics, the asphalt index) can disagree.

const LINE_TILE = 192; // world units the worn-paint pattern repeats over
const EDGE_BLADE = '#639a4a'; // the lighter green of the newest blades at the edge
const EARTH_TILE = 192;  // world units the run-off pattern repeats over
const EARTH = '#40382a';      // packed earth: dark, barely greener than the shadowed grass
const EARTH_DARK = '#332c21';
const EARTH_LIGHT = '#4d442f';
const EARTH_DRY = '#5b5038';
const SAND_WIDTH = 5;   // how far the packed earth reaches outside the asphalt's edge

/**
 * A patch of run-off: packed earth and gravel with stretches worn through to the grass.
 *
 * The holes are the point. A band of sand stroked along the edge would have a clean outer boundary, and
 * a clean boundary is exactly what makes the surface read as a drawing; with holes in the tile, the
 * grass shows through in patches and the band's edge breaks up on its own.
 */
function makeSandTile(createCanvas, rng, colour) {
  const canvas = createCanvas(EARTH_TILE, EARTH_TILE);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = colour;
  ctx.fillRect(0, 0, EARTH_TILE, EARTH_TILE);
  // Fine grit, not blobs: a run-off is thousands of small grains, and grain size is what separates
  // "dirt" from "splodges" at the size the surface ends up being seen at.
  for (let i = 0; i < 10400; i++) {
    ctx.fillStyle = rng() < 0.5 ? EARTH_DARK : (rng() < 0.75 ? EARTH_LIGHT : EARTH_DRY);
    ctx.globalAlpha = 0.10 + rng() * 0.3;
    ctx.fillRect(rng() * EARTH_TILE, rng() * EARTH_TILE, 0.8 + rng() * 1.7, 0.8 + rng() * 1.7);
  }
  ctx.globalAlpha = 1;
  // Worn through in SMALL patches (ellipses): a rectangular hole is a rectangular hole however small.
  // And not worn THROUGH: the band's holes must stay lighter earth, never a window. What a hole in the
  // earth would show underneath is the white line it was laid over, so an opaque hole reads as a bright
  // speck of line under the dirt — i.e. as a hole in the earth and not as thin earth. The earth keeps at
  // least 60% of its alpha, which is dirt over paint.
  ctx.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 184; i++) {
    ctx.globalAlpha = 0.12 + rng() * 0.28;
    ctx.beginPath();
    ctx.ellipse(rng() * EARTH_TILE, rng() * EARTH_TILE, 0.8 + rng() * 3.2, 0.7 + rng() * 2.6, rng() * Math.PI, 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  return canvas;
}

/** Strokes a polyline that may carry nulls (the boundary breaks where the road is too narrow). */
function strokeRun(ctx, points) {
  ctx.beginPath();
  let drawing = false;
  for (const point of points ?? []) {
    if (!point) { drawing = false; continue; }
    if (drawing) ctx.lineTo(point.x, point.y);
    else { ctx.moveTo(point.x, point.y); drawing = true; }
  }
  ctx.stroke();
}

/** A patch of worn paint: the line's colour with grit on it and parts worn thin. */
function makeWornLineTile(createCanvas, rng, colour) {
  const canvas = createCanvas(LINE_TILE, LINE_TILE);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = colour;
  ctx.fillRect(0, 0, LINE_TILE, LINE_TILE);
  for (let i = 0; i < 1680; i++) {
    ctx.fillStyle = rng() < 0.5 ? '#8b8b86' : '#ffffff';
    ctx.globalAlpha = 0.10 + rng() * 0.24;
    ctx.fillRect(rng() * LINE_TILE, rng() * LINE_TILE, 1 + rng() * 2.4, 1 + rng() * 1.6);
  }
  ctx.globalAlpha = 1;
  // Worn THIN, not punched through: the paint thins out and the asphalt shows through it, which is what
  // a worn line looks like — a clearRect leaves a hole with hard edges, and (on the stretches where the
  // run-off covers the line's outer half) a hole in the earth's band over it reads as a bright speck.
  ctx.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 64; i++) {
    ctx.globalAlpha = 0.3 + rng() * 0.5;
    ctx.fillRect(rng() * LINE_TILE, rng() * LINE_TILE, 1 + rng() * 5, 1 + rng() * 2.4);
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  return canvas;
}

/**
 * A pattern from one of the tiles, shifted so that two lengths of line don't wear in the same places.
 * The tile repeats every LINE_TILE units — that repetition is what the eye picks up, and every run
 * starting at phase zero would put the same worn patch at the same distance along every edge.
 */
function patternAt(ctx, tile, x, y) {
  return repeatingPattern(ctx, tile, x, y);
}

/**
 * The packed earth outside the asphalt: a stroke of sand along the edge, its outer boundary broken by
 * the holes in the tile (the grass shows through in patches) and by the blades drawn over it after.
 * The second, wider and fainter pass is what makes the band fade out instead of stopping.
 */
function paintRunOff(ctx, run, sand) {
  if (!sand) return;
  ctx.strokeStyle = sand;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.lineWidth = SAND_WIDTH;
  strokeRun(ctx, offsetChain(run.points, run.k, SAND_WIDTH / 2));
  ctx.globalAlpha = 0.26;
  ctx.lineWidth = SAND_WIDTH * 1.7;
  strokeRun(ctx, offsetChain(run.points, run.k, SAND_WIDTH * 0.85));
  ctx.globalAlpha = 1;
}

function scatterEdgeWear(ctx, points, k, rng, p) {
  let carried = rng() * 9; // carried distance: the tufts must not line up between two runs
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (!(len > 0.01)) continue;
    const nx = (-dy / len) * k; // outward
    const ny = (dx / len) * k;
    let t = carried / len;
    while (t < 1) {
      const x = a.x + dx * t;
      const y = a.y + dy * t;
      // The clump grows on the grass side and only LEANS over the edge: blades landing on the tarmac
      // read as weeds through the asphalt, which is not what a track looks like. With the line ON the
      // edge, this starts where the line's outer half is, so the blades bite into the paint.
      const out = 0.4 + rng() * 3.6;
      tuft(ctx, x + nx * out, y + ny * out, nx, ny, rng, p);
      if (rng() < 0.45 && rng() < 0.5) tuft(ctx, x + nx * (6 + rng() * 7), y + ny * (6 + rng() * 7), nx, ny, rng, p);
      if (rng() < 0.5) grit(ctx, x - nx * rng() * 3, y - ny * rng() * 3, rng, p);
      t += (1.8 + rng() * 3.4) / len;
    }
    carried = (t - 1) * len;
  }
}

/**
 * One clump of grass at the edge: a few blades, each a thin triangle growing AWAY from the tarmac
 * (a blade leans outward, it does not sit there as a blob), in the greens around the base colour.
 * Semi-transparent on purpose: the blades have to break the line underneath, not cover it.
 */
function tuft(ctx, x, y, nx, ny, rng, p) {
  const blades = 3 + Math.floor(rng() * 4);
  const outward = Math.atan2(ny, nx);
  for (let i = 0; i < blades; i++) {
    const tone = rng();
    const angle = outward + (rng() - 0.5) * 1.4;
    const len = 1.8 + rng() * 3.2;
    const half = 0.24 + rng() * 0.28;
    const bx = x + (rng() - 0.5) * 3.4;
    const by = y + (rng() - 0.5) * 3.4;
    ctx.fillStyle = tone < 0.35 ? p.grassDark : (tone < 0.75 ? p.grassLight : EDGE_BLADE);
    ctx.globalAlpha = 0.32 + rng() * 0.45;
    ctx.beginPath();
    ctx.moveTo(bx - half, by);
    ctx.lineTo(bx + half, by);
    ctx.lineTo(bx + Math.cos(angle) * len, by + Math.sin(angle) * len);
    ctx.closePath();
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/** A speck of dirt or gravel on the tarmac side of the edge. */
function grit(ctx, x, y, rng, p) {
  ctx.fillStyle = rng() < 0.5 ? p.asphaltDark : '#70717a';
  ctx.globalAlpha = 0.16 + rng() * 0.26;
  ctx.beginPath();
  ctx.arc(x, y, 0.4 + rng() * 0.9, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
}

/** Every piece of asphalt the surface is made of, in painting order: the bands (the road, the pit
 *  lane) and the areas, SAMPLED from the curves the file carries (trackGeometry.samplePiece).
 *
 * A closed piece comes back with its first point repeated at the end: everything below walks a run
 * as an open polyline, and that extra point is what turns a circuit into a ring whose seam is an
 * ordinary vertex â€” the painter used to rebuild the ring by hand (closeRing) out of the loose ends
 * of the exported segments, and to guess which of THOSE were actually connected (chainRuns). */
function collectPieces(track, roadWidth) {
  const pieces = [];
  for (const piece of track.pieces ?? []) {
    const sampled = samplePiece(piece, { roadWidth });
    if (sampled.points.length < 2) continue;
    const ring = sampled.closed ? [...sampled.points, sampled.points[0]] : sampled.points;
    // The file says `w` (a width per node, which is what a narrow section or a junction is); inside
    // the painter a road point has always carried `width`, so the two names meet exactly here.
    const points = ring.map((point) => ({ x: point.x, y: point.y, width: point.w }));
    const pit = piece.pit === true || piece.lane === 'pit';
    const surface = SURFACE_STYLES.includes(piece.surface) ? piece.surface : DEFAULT_SURFACE;
    if (sampled.band) pieces.push(corridorPiece([points], roadWidth, piece.id ?? '', pit, surface));
    else pieces.push({ kind: 'area', pit, surface, points });
  }
  return pieces;
}

/**
 * The start/finish marking at the track's first checkpoint line: the closest main-road crossing gets
 * the checkered line, the closest pit-lane crossing a plain white one.
 *
 * The marking spans the road, not the line as it was drawn: a checkpoint is placed with two clicks in
 * the editor, so its line is a rough indication of *where* the line is â€” often wider than the
 * asphalt (hot_road's is three times as wide) or narrower than it (tortellino's). Painting it as
 * authored would stick out over the grass or leave a stub in the middle of the road. The line still
 * gives the marking its direction, and each road's own width decides where it ends.
 */
function paintStartBand(ctx, cp, pieces, roadWidth, p) {
  const dx = cp.x2 - cp.x1;
  const dy = cp.y2 - cp.y1;
  const lineLength = Math.hypot(dx, dy);
  if (lineLength < 1) return;
  const ux = dx / lineLength;
  const uy = dy / lineLength;
  const px = -uy;
  const py = ux;
  const p1 = { x: cp.x1, y: cp.y1 };
  const p2 = { x: cp.x2, y: cp.y2 };
  const midX = (cp.x1 + cp.x2) / 2;
  const midY = (cp.y1 + cp.y2) / 2;
  let main = null;
  let pit = null;

  for (const piece of pieces) {
    if (piece.kind !== 'path' && piece.kind !== 'area') continue;
    const hit = piece.kind === 'area'
      ? areaCrossing(piece, p1, p2)
      : startCrossing(piece, p1, p2, roadWidth);
    if (!hit) continue;
    const candidate = {
      ...hit,
      length: piece.kind === 'area' ? hit.length : hit.halfWidth * 2,
      dist: Math.hypot(hit.x - midX, hit.y - midY),
    };
    const current = piece.pit ? pit : main;
    if (!current || candidate.dist < current.dist) {
      if (piece.pit) pit = candidate;
      else main = candidate;
    }
  }

  if (main) paintStartCheckers(ctx, main, ux, uy, px, py, p);
  if (pit) paintStartPitLine(ctx, pit, ux, uy, px, py, p);
}

/** The checkered line across the main road: one checker deep, `size` world units per square. */
function paintStartCheckers(ctx, hit, ux, uy, px, py, p) {
  const cols = Math.max(4, Math.round(hit.length / START_BAND_CELL));
  const size = hit.length / cols;
  for (let row = 0; row < START_BAND_ROWS; row++) {
    const depthOffset = (row - (START_BAND_ROWS - 1) / 2) * size;
    for (let col = 0; col < cols; col++) {
      ctx.fillStyle = (col + row) % 2 ? p.checkerB : p.checkerA;
      const along = (col + 0.5 - cols / 2) * size;
      const cx = hit.x + along * ux + depthOffset * px;
      const cy = hit.y + along * uy + depthOffset * py;
      const hx = (size / 2) * ux;
      const hy = (size / 2) * uy;
      const dxp = (size / 2) * px;
      const dyp = (size / 2) * py;
      ctx.beginPath();
      ctx.moveTo(cx + hx + dxp, cy + hy + dyp);
      ctx.lineTo(cx - hx + dxp, cy - hy + dyp);
      ctx.lineTo(cx - hx - dxp, cy - hy - dyp);
      ctx.lineTo(cx + hx - dxp, cy + hy - dyp);
      ctx.closePath();
      ctx.fill();
    }
  }
}

/** The pit lane keeps the line but not the checkers: a plain white stroke, thinner than the band. */
function paintStartPitLine(ctx, hit, ux, uy, px, py, p) {
  const hx = (hit.length / 2) * ux;
  const hy = (hit.length / 2) * uy;
  const hw = PIT_START_LINE_WIDTH / 2;
  ctx.fillStyle = p.checkerA;
  ctx.beginPath();
  ctx.moveTo(hit.x + hx + hw * px, hit.y + hy + hw * py);
  ctx.lineTo(hit.x - hx + hw * px, hit.y - hy + hw * py);
  ctx.lineTo(hit.x - hx - hw * px, hit.y - hy - hw * py);
  ctx.lineTo(hit.x + hx - hw * px, hit.y + hy - hw * py);
  ctx.closePath();
  ctx.fill();
}

/** Pit box: a slab with garage dividers along its long side, so the pit lane reads as built, not bare. */
function paintPitBox(ctx, pb, p) {
  const x = Math.min(pb.x1, pb.x2);
  const y = Math.min(pb.y1, pb.y2);
  const w = Math.abs(pb.x2 - pb.x1);
  const h = Math.abs(pb.y2 - pb.y1);
  if (w < 1 || h < 1) return;

  ctx.globalAlpha = 0.5;
  ctx.fillStyle = p.pitSlab;
  ctx.fillRect(x, y, w, h);
  ctx.globalAlpha = 0.3;
  ctx.strokeStyle = p.line;
  ctx.lineWidth = 3;
  ctx.beginPath();
  const horizontal = w >= h;
  const long = horizontal ? w : h;
  const slots = Math.max(2, Math.round(long / 120));
  for (let i = 1; i < slots; i++) {
    if (horizontal) {
      const gx = x + (long / slots) * i;
      ctx.moveTo(gx, y);
      ctx.lineTo(gx, y + h);
    } else {
      const gy = y + (long / slots) * i;
      ctx.moveTo(x, gy);
      ctx.lineTo(x + w, gy);
    }
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.lineWidth = 4;
  ctx.strokeRect(x, y, w, h);
}

// â”€â”€ barriers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// The obstacles the editor places by hand (walls, polygon walls, blocks) are painted as NARROW ROADS:
// the corridor machinery already does the geometry — per-point width, mitered faces, round joins and
// the union outline, so two barriers that meet share one contour instead of drawing a line each. What
// a barrier is MADE of comes from its `material` (see materials.js, the catalogue: the name is what
// the track file carries, the look is free to change there).
//
// Decoration only: the bodies the cars hit come from the file, so nothing here can move a wall.

// Where the sun is: the shadow every barrier casts, down and to the right. A fixed offset (not one per
// shape) is what makes a track's barriers read as the same object lit once.
const BARRIER_SHADOW = { x: 3, y: 4 };
// How far a barrier's corner may stick out when it is mitred, in half-widths, before it is cut straight
// across instead (the same kind of limit roadBoundary uses on the asphalt's corners).
const BARRIER_MITER_LIMIT = 2.5;

/** The grain of a material: stains, coarse aggregate, air pits and a hairline crack or two — a flat,
 * slightly mottled base is exactly what a poured-and-polished surface looks like, i.e. plastic. */
function makeBarrierTile(createCanvas, rng, look, size = 128) {
  const canvas = createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = look.base;
  ctx.fillRect(0, 0, size, size);
  const area = (size * size) / (128 * 128); // the tile is a fixed size on screen: the grain scales with it

  // Stains first, under everything: small patches of a dirtier grey, drawn as growing ellipses so the
  // edge fades out instead of ending on a line. Kept small and faint — big soft patches over a light
  // base are clouds, and clouds read as polished stone rather than cast concrete.
  for (let i = 0; i < 14 * area; i++) {
    const cx = rng() * size;
    const cy = rng() * size;
    const rx = 5 + rng() * 15;
    const ry = 4 + rng() * 13;
    const turn = rng() * Math.PI;
    ctx.fillStyle = rng() < 0.5 ? look.dark : look.light;
    for (let step = 3; step >= 1; step--) {
      ctx.globalAlpha = 0.012 + 0.016 * step;
      ctx.beginPath();
      ctx.ellipse(cx, cy, (rx * step) / 3, (ry * step) / 3, turn, 0, TAU);
      ctx.fill();
    }
  }

  // Aggregate: the grain is all a concrete wall has to say it is stone, so it is contrasty and measured
  // in dots about one to three units across (a soft haze just looks like a gradient).
  for (let i = 0; i < 4200 * area; i++) {
    const tone = rng();
    ctx.fillStyle = tone < 0.34 ? look.dark : (tone < 0.78 ? look.light : '#6b6964');
    ctx.globalAlpha = 0.1 + rng() * 0.32;
    ctx.beginPath();
    ctx.arc(rng() * size, rng() * size, 0.6 + rng() * 2, 0, TAU);
    ctx.fill();
  }

  // Air pits: the little dark holes of a cast surface, and the thing that stops it reading as smooth.
  ctx.fillStyle = look.joint;
  for (let i = 0; i < 170 * area; i++) {
    ctx.globalAlpha = 0.16 + rng() * 0.32;
    ctx.beginPath();
    ctx.arc(rng() * size, rng() * size, 0.5 + rng() * 1.1, 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // A couple of hairline cracks, walking across the tile.
  ctx.strokeStyle = look.joint;
  for (let i = 0; i < 3 * area; i++) {
    ctx.globalAlpha = 0.1 + rng() * 0.12;
    ctx.lineWidth = 0.5 + rng() * 0.6;
    ctx.beginPath();
    let x = rng() * size;
    let y = rng() * size;
    ctx.moveTo(x, y);
    for (let step = 0; step < 4; step++) {
      x += (rng() - 0.5) * 26;
      y += (rng() - 0.5) * 26;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  return canvas;
}

/** Looks up the already-decoded drawable for a material's image, if the caller supplied one (see
 * paintTrack's `opts.images`, keyed by the material's `image.src`) — absent while it's still
 * loading, which the callers below treat as "just the flat base colour, no stamps yet". */
function resolveImage(opts, src) {
  return opts?.images?.[src] ?? null;
}

/** A `size` by `size` pattern tile filled with a material's own picture (a wall's tiled texture). */
function tileFromImage(createCanvas, image, size) {
  const canvas = createCanvas(size, size);
  canvas.getContext('2d').drawImage(image, 0, 0, size, size);
  return canvas;
}

/** Image sprites (tyres, fence strips, seating or blocks) carry their own transparent detail rather
 * than being painted as a solid wall with faces and a concrete contour. */
function isSpriteLook(look) {
  return Boolean(look?.unit || look?.strip || look?.composite || look?.variants?.length);
}

/** A look that is ONE sprite fitted to a rectangle (`stands`, `houses`): its `variants` carry the files. */
function isBlockLook(look) {
  return Boolean(look?.composite || look?.variants?.length);
}

/** The variant of sprite this obstacle asks for, or null when its material isn't one sprite at all.
 * Only a RECT can be one: the sprite is fitted to the rectangle, and a rectangle is the only shape it
 * can be fitted to. Anything else that names the material is painted as a wall instead. */
function lookVariant(look, obstacle) {
  if (!isBlockLook(look) || obstacle?.type !== 'rect') return null;
  if (look.composite) return 0;
  const index = Number.isInteger(obstacle.variant) ? obstacle.variant : 0;
  return look.variants[index] ? index : 0;
}

/**
 * One obstacle as a strip: the line it runs along and how wide it is. What the editor's four obstacle
 * types have in common — a wall IS that strip, a polygon wall is one that bends, a block is a short
 * thick one. A circle isn't a strip (no direction to stamp along) — see barrierCircles instead.
 */
function barrierStrips(track) {
  const strips = [];
  for (const obstacle of track.obstacles ?? []) {
    const wanted = materialNameFor(track, obstacle);
    const look = materialLook(track, wanted);
    // A sprite material has to be a RECT: one sprite is fitted to the rectangle, so on a wall or a block
    // there is nothing for it to be fitted TO. An obstacle that names it anyway is painted as the default
    // material rather than as a sprite that cannot fit — tracks are hand-edited, and a wrong shape must
    // not be able to make the painter draw nonsense.
    const variant = lookVariant(look, obstacle);
    const material = isBlockLook(look) && variant === null ? DEFAULT_MATERIAL : wanted;
    if (obstacle.type === 'wall') {
      strips.push({
        obstacle,
        spine: [{ x: obstacle.x1, y: obstacle.y1 }, { x: obstacle.x2, y: obstacle.y2 }],
        thickness: obstacle.thickness ?? 20,
        material,
      });
    } else if (obstacle.type === 'poly') {
      // `points` is the polyline the game reads (curves already flattened); `nodes` is the same thing
      // with the Bezier handles kept for the editor, and is only there when there is a curve to keep.
      const source = obstacle.points?.length ? obstacle.points : obstacle.nodes ?? [];
      const spine = source.map((point) => ({ x: point.x, y: point.y }));
      if (spine.length < 2) continue;
      strips.push({ obstacle, spine, thickness: obstacle.thickness ?? 8, material });
    } else if (obstacle.type === 'rect') {
      // A rectangle's spine (which side it runs along, and how thick it is across) is the same rule the
      // editor uses: see rectSpine in tyreLayout.js for why a sprite material is the exception.
      const { points, thickness } = rectSpine(obstacle, variant !== null);
      strips.push({ obstacle, spine: points, thickness, material, variant });
    }
  }
  return strips;
}

/** A circle obstacle (a single stack of tyres, seen from above): no direction to stamp along, so it
 * gets its own single centred stamp instead of barrierStrips' chain treatment. */
function barrierCircles(track) {
  const circles = [];
  for (const obstacle of track.obstacles ?? []) {
    if (obstacle.type !== 'circle') continue;
    circles.push({ id: obstacle.id, x: obstacle.x, y: obstacle.y, radius: obstacle.radius,
      material: materialNameFor(track, obstacle), decor: obstacle.decor === true });
  }
  return circles;
}

/** Where a chain is at arc-length `s` from its start: position, direction and width there — and where the
 * tyres of a pile sit, and how one is drawn: all of that lives in tyreLayout.js, shared with the track
 * editor so its preview piles the tyres exactly where the game will. */

/** Every shadow of every pile, in one pass (see paintBarriers). The shadow is scaled with the tyre: a
 * few units of offset that read as a soft edge under a 20-unit barrier put a 10-unit tyre's shadow
 * almost entirely out from under it, as a second dark tyre in the row. */
function paintPileShadows(ctx, piles) {
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.16)';
  for (const pile of piles) {
    const offset = pile.unit * 0.28;
    const ox = (BARRIER_SHADOW.x / 5) * offset;
    const oy = (BARRIER_SHADOW.y / 5) * offset;
    for (const stamp of pile.stamps) {
      ctx.beginPath();
      ctx.arc(stamp.x + ox, stamp.y + oy, pile.unit * 0.5, 0, TAU);
      ctx.fill();
    }
  }
  ctx.restore();
}

function paintPileTyres(ctx, piles) {
  if (!piles.length) return;
  // A tyre is drawn several times smaller than it is (52 px of sprite over 10 units), and at that ratio
  // the default filter turns the tread into a blur — the same reason the blocks ask for the good one.
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const draws = piles.flatMap(pile => pile.stamps.map(stamp => ({ stamp, set: pile.tyres })));
  draws.sort((a, b) => compareTyreDepth(a.stamp, b.stamp));
  for (const { stamp, set } of draws) {
    ctx.save();
    ctx.translate(stamp.x, stamp.y);
    drawTyreCell(ctx, set, stamp);
    ctx.restore();
  }
  ctx.restore();
}

/**
 * A barrier's corners, welded.
 *
 * Two strips that get chained are joined by a straight hop from the end of one spine to the start of the
 * next. That is invisible when the hop lines up with the direction — a wall carrying straight on, or two
 * arms that happen to share a centre line — but not when it doesn't, which is the normal case for two
 * barriers meeting at a square angle: each spine ends at the OTHER barrier's centre line, so the two ends
 * are offset by half a thickness each way and the hop runs diagonally across the corner. Drawn as it
 * comes, the corner turns twice, around a little diagonal wedge.
 *
 * The corner the two walls really make is where their spines cross, so the hop is replaced by it: one
 * bend, which the mitre then squares off. A hop is recognisable by being short — that is how the strips
 * got chained in the first place — while the stretches it joins are long.
 */
function weldBarrierCorners(chain) {
  const out = [chain[0]];
  for (let i = 1; i < chain.length - 1; i++) {
    const previous = out[out.length - 1];
    const point = chain[i];
    const next = chain[i + 1];
    const hop = Math.hypot(point.x - previous.x, point.y - previous.y);
    const tolerance = (widthOf(previous, 20) + widthOf(point, 20)) / 2;
    const ahead = Math.hypot(next.x - point.x, next.y - point.y);
    if (hop > 0.01 && hop <= tolerance && ahead > hop) {
      const u1 = unit(point.x - previous.x, point.y - previous.y);
      const u2 = unit(next.x - point.x, next.y - point.y);
      const cross = u1 && u2 ? u1.x * u2.y - u1.y * u2.x : 0;
      if (Math.abs(cross) > 0.02) {
        const t = ((point.x - previous.x) * u2.y - (point.y - previous.y) * u2.x) / cross;
        const corner = { x: previous.x + u1.x * t, y: previous.y + u1.y * t };
        // Only a crossing just past the hop counts: two nearly parallel stretches meet somewhere far
        // down the wall, and that is not a corner, it is arithmetic.
        if (t > 0 && Math.hypot(corner.x - point.x, corner.y - point.y) <= hop * 2) {
          // The corner keeps the wider of the two thicknesses — where two barriers of different size
          // meet, the junction is as wide as the wider one (the same rule the editor welds widths with).
          out[out.length - 1] = { x: corner.x, y: corner.y, width: Math.max(widthOf(previous, 20), widthOf(point, 20)) };
          continue;
        }
      }
    }
    out.push(point);
  }
  out.push(chain[chain.length - 1]);
  return out;
}

/**
 * One face of a barrier: a band along the centre line, offset a hair off it and narrower than the strip.
 * The colour and the alpha come from the look; `fill` (a patterned tile in the face's own colour) wins
 * over `colour` when the caller built one.
 */
function paintBarrierFace(ctx, chain, face) {
  if (!face) return;
  const band = chain.map((point) => ({
    x: point.x + face.offset,
    y: point.y + face.offset,
    width: (point.width ?? 20) * face.scale,
  }));
  const fill = face.fill ?? face.colour;
  const half = (widthOf(chain[0], 20) * face.scale) / 2;
  const alpha = ctx.globalAlpha;
  ctx.globalAlpha = face.alpha ?? 1;
  paintRoadSurface(ctx, [band], fill);
  paintBarrierMiters(ctx, band, fill, () => half);
  ctx.globalAlpha = alpha;
}

/**
 * The corners of a barrier, squared off.
 *
 * The body of a barrier is drawn the way the asphalt is — a trapezoid per stretch, plus a round join at
 * each bend — which is right for a road and wrong for a wall: two walls placed end to end at an angle
 * came out with a rounded corner and a little notch of base showing on every face that stops short of
 * it, i.e. a visible division at the joint instead of one wall making an angle.
 *
 * The patch is the piece of strip neither stretch reaches: a quad from the corner to the point where the
 * two outer edges meet (the miter). Where the bend is too sharp to miter, the corner is cut straight
 * across instead — the same choice `roadBoundary` makes for the same reason.
 */
function paintBarrierMiters(ctx, chain, fill, halfAt) {
  ctx.fillStyle = fill;
  for (let i = 1; i < chain.length - 1; i++) {
    const previous = chain[i - 1], point = chain[i], next = chain[i + 1];
    const u1 = unit(point.x - previous.x, point.y - previous.y);
    const u2 = unit(next.x - point.x, next.y - point.y);
    if (!u1 || !u2) continue;
    const cross = u1.x * u2.y - u1.y * u2.x;
    if (Math.abs(cross) < 0.02) continue; // straight on (or doubled back): there is no corner here
    const sign = cross > 0 ? -1 : 1;      // which normal points out of the turn
    const h = halfAt(point);
    const from = { x: point.x - u1.y * sign * h, y: point.y + u1.x * sign * h };
    const to = { x: point.x - u2.y * sign * h, y: point.y + u2.x * sign * h };
    const t = ((to.x - from.x) * u2.y - (to.y - from.y) * u2.x) / cross;
    const miter = { x: from.x + u1.x * t, y: from.y + u1.y * t };
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineTo(from.x, from.y);
    if (Math.hypot(miter.x - point.x, miter.y - point.y) <= h * BARRIER_MITER_LIMIT) {
      ctx.lineTo(miter.x, miter.y);
    }
    ctx.lineTo(to.x, to.y);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * Everything the barriers are: the fill, the shadow they cast and their contour.
 *
 * The obstacles are painted one Z LAYER at a time, lowest first (see inPaintLayers): a figure with a
 * z-index lies over one without, and each layer is finished — shadows, bodies, contours, piles —
 * before the next starts, so no pass of a lower layer can leak over a higher one. Within a layer the
 * strips are CHAINED per material, and that is what makes two walls the author placed end to end
 * behave like the one barrier they are: the filler joins the corner properly (round joins, per
 * -point width) instead of two square ends butting into a notch, and the union outline goes around the
 * corner as a single silhouette — so there is no line where nothing actually ends. It is the same
 * treatment the asphalt gets, for the same reason.
 *
 * A look that describes TYRES (a tyre size with a texture) skips all of that: it lays the texture
 * down as whole tyres instead (tyreChainStamps/tyreCircleStamps + paintPileTyres). Whatever the look,
 * the shadow and the union outline/contour are shared — they are shape-only and don't care what is
 * being painted.
 */
function paintBarriers(ctx, track, createCanvas, seed, opts) {
  for (const layer of inPaintLayers(track.obstacles)) {
    paintBarrierLayer(ctx, { ...track, obstacles: layer.items }, createCanvas, seed, opts);
  }
}

function paintBarrierLayer(ctx, track, createCanvas, seed, opts) {
  // Share decoded images/paint by material and variant. Walls are chained within their group;
  // rectangle sprites retain separate geometry even when their material and variant match.
  const byMaterial = new Map();
  for (const strip of barrierStrips(track)) {
    // Keep invisible obstacles in the track and occupancy mask; only omit their visual passes.
    if (materialLook(track, strip.material).invisible) continue;
    const key = `${strip.material}|${strip.variant ?? ''}`;
    if (!byMaterial.has(key)) byMaterial.set(key, { material: strip.material, variant: strip.variant, strips: [] });
    byMaterial.get(key).strips.push(strip);
  }
  const circlesByMaterial = new Map();
  for (const circle of barrierCircles(track)) {
    if (materialLook(track, circle.material).invisible) continue;
    if (!circlesByMaterial.has(circle.material)) circlesByMaterial.set(circle.material, []);
    circlesByMaterial.get(circle.material).push(circle);
  }
  if (!byMaterial.size && !circlesByMaterial.size) return;

  // One set of textures per material this track actually uses, built here rather than up front: the
  // look can be overridden by the track (see materials.js), so it isn't known until the track is.
  const usedMaterials = new Set([...byMaterial.values()].map((group) => group.material));
  const looks = new Map();
  for (const name of new Set([...usedMaterials, ...circlesByMaterial.keys()])) {
    const look = materialLook(track, name);
    const lookRng = mulberry32(seed ^ hashString(`barrier:${name}`));
    if (look.strip === 'fence') {
      looks.set(name, { ...look, source: resolveImage(opts, look.image.src) });
      continue;
    }
    if (look.unit) {
      // Sprite measurement uses native image pixels, unlike the high-resolution procedural tiles.
      looks.set(name, { ...look, tyres: tyreSet(look, look.image ? resolveImage(opts, look.image.src) : null,
        opts.createCanvas ?? defaultCreateCanvas) });
      continue;
    }
    if (look.composite) {
      looks.set(name, { ...look, source: resolveImage(opts, look.image.src) });
      continue;
    }
    if (isBlockLook(look)) {
      // Stands: one sprite per variant, in the same order as `variants`, so a strip can pick its own.
      looks.set(name, { ...look, sprites: look.variants.map((variant) => resolveImage(opts, variant.src)) });
      continue;
    }
    // A textured wall: the material's own tiled picture IS its surface, so the procedural grain and the
    // paint bands are left out — they are exactly what the picture replaces. The contour, the dirt at
    // its foot and the shadow stay: they read the same over a photo as over paint. With no decoded file
    // this falls through to the painted wall below (a plainer wall, never a missing one).
    const image = look.image ? resolveImage(opts, look.image.src) : null;
    if (image) {
      looks.set(name, {
        ...look,
        fill: repeatingPattern(ctx, tileFromImage(createCanvas, image, 192)),
        crown: null,
        shade: null,
      });
      continue;
    }
    // The two faces get their own tiles in their own colour: a band of flat paint over a wall is the one
    // thing that really reads as plastic, and a face is only as concrete as its grain. Same rng, so the
    // three tiles are three different draws.
    const face = (spec) => (spec
      ? {
        ...spec,
        fill: repeatingPattern(ctx,
          makeBarrierTile(createCanvas, lookRng, { ...look, base: spec.colour }, 96),
        ),
      }
      : null);
    looks.set(name, {
      ...look,
      // 192 units of tile for the body (a wall is long: the smaller the tile, the sooner a stain comes
      // back) and 96 for the faces, which are narrow and never show a whole tile anyway.
      fill: repeatingPattern(ctx, makeBarrierTile(createCanvas, lookRng, look, 192)),
      crown: face(look.crown),
      shade: face(look.shade),
    });
  }

  const groups = [];
  for (const group of byMaterial.values()) {
    const strips = group.strips;
    // The duplicate point a chain leaves where two strips meet has to go: a zero-length segment has
    // no direction, and the joins are computed from the two directions at a node — with one of them
    // zero the filler gives up, and the outside of that corner stays a notch of grass.
    const runs = strips.map((strip) => strip.spine.map((point) => ({ x: point.x, y: point.y, width: strip.thickness })));
    // A block owns its rectangle. Chaining nearby blocks bridges empty ground, invents diagonal
    // shadows and reverses/rotates sprites when a neighbouring spine is traversed backwards.
    const chains = isBlockLook(looks.get(group.material)) || looks.get(group.material).strip ? runs : chainRuns(runs, strips[0].thickness)
      .map((chain) => withoutDuplicates(weldBarrierCorners(chain)))
      .filter((chain) => chain.length > 1);
    groups.push({ material: group.material, variant: group.variant, look: looks.get(group.material), chains, strips });
  }
  const pieces = groups.flatMap((group) => group.chains.map((chain) => ({
    kind: 'path',
    pit: false,
    mitre: true, // a barrier corners squarely: see roadBoundary
    surface: group.material,
    fallbackWidth: widthOf(chain[0], 20),
    runs: [chain],
  })));
  // The walls' shadow and contour are worked out on the SOLID pieces alone — the walls and the blocks
  // built of colour — and painted before the piles. A pile of tyres lying on a wall is not part of the
  // wall's silhouette, and letting it cut the outline left that stretch of wall unshadowed and unedged:
  // visibly lighter than the wall beside it, which reads as a pass that did not run. The tyres cover it
  // afterwards instead, so the edge is hidden exactly where a tyre is and shows in the gaps, which is
  // what a wall behind a pile does.
  //
  // A grandstand is left in for the SHADOW (it is a big flat block: without a shadow it doesn't sit on
  // the ground) and left out of the contour, which is its own frame in the sprite.
  const contourPieces = pieces.filter((piece) => !isSpriteLook(looks.get(piece.surface)));
  // …and the shadow follows the same rule as the plinth: a sprite on its own base (a grandstand) casts
  // one, a sprite that lies on the track (a cluster of houses, whose picture carries its own soft
  // shadows) does not — traced around the rectangle it would run across the ground showing through it.
  const shadowPieces = pieces.filter((piece) => {
    const look = looks.get(piece.surface);
    return !look.unit && (!isSpriteLook(look) || Boolean(look.base));
  });
  const outlines = unionOutline(contourPieces);
  // Without blocks that cast shadows, the solid contour is also the shadow silhouette.
  const shadowOutlines = shadowPieces.length === contourPieces.length ? outlines : unionOutline(shadowPieces);

  // Every pile of tyres on the track, as stamps collected BEFORE anything is drawn: a pile's shadows
  // and its tyres are then drawn in two passes over all of them. Drawn one pile at a time, the shadows
  // of the next barrier fall on the tyres of the one before (two tyre walls side by side are the
  // normal case), which reads as a row of dark tyres and looks like a bug.
  const piles = [];
  for (const group of groups) {
    if (!group.look.unit) continue;
    // Stamped with the very function the track editor stamps with (tyreStampsForObstacle), one obstacle
    // at a time and not along the chains above: a chain exists so that two walls set end to end are
    // drawn as one shape, and its length covers both, which would push the tyres about to keep the
    // spacing even across the joint. One obstacle is one pile, and the two renderers cannot drift.
    for (const strip of group.strips) {
      piles.push({
        look: group.look,
        tyres: group.look.tyres,
        unit: group.look.unit,
        // No sheet yet: the pile keeps its place in the list (its base colour is painted below) but there
        // are no stamps to make.
        stamps: group.look.tyres ? tyreStampsForObstacle(strip.obstacle, group.look.tyres) : [],
      });
    }
  }
  for (const [, circles] of circlesByMaterial) {
    for (const circle of circles) {
      const look = looks.get(circle.material);
      if (!look.unit) continue;
      piles.push({
        look, tyres: look.tyres, unit: look.unit, circle,
        stamps: look.tyres ? tyreCircleStamps(circle, look.tyres) : [],
      });
    }
  }

  ctx.save();
  ctx.translate(BARRIER_SHADOW.x, BARRIER_SHADOW.y);
  for (const outline of shadowOutlines) {
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.10)';
    ctx.lineWidth = 15;
    strokeRun(ctx, outline.points);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.16)';
    ctx.lineWidth = 7;
    strokeRun(ctx, outline.points);
  }
  ctx.restore();

  for (const group of groups) {
    if (isSpriteLook(group.look)) continue;
    for (const chain of group.chains) {
      paintRoadSurface(ctx, [chain], group.look.fill);
      // The body's own corners come from the piece boundaries, which are square for a barrier (see the
      // `mitre` flag on the pieces): the fill has to reach them, or the rounded join it is drawn with
      // leaves the corner of the wall round while its contour is square.
      paintBarrierMiters(ctx, chain, group.look.fill, (point) => widthOf(point, 20) / 2);
      // The faces: the lit top a hair up and left of the centre line, the shaded side a hair down and
      // right. The PAIR is what gives the strip thickness — one lit band on its own is a sheen.
      // Their corners are mitred too: a face that stops short of the bend leaves a notch of base
      // showing, and that notch IS the visible joint between two walls placed end to end.
      paintBarrierFace(ctx, chain, group.look.crown);
      paintBarrierFace(ctx, chain, group.look.shade);
    }
  }

  // A decor circle has no strip to follow and no pile to lay: it is a disc of its texture, inside its own
  // contour. Tyre looks are skipped here because the pile pass below is what draws them.
  for (const circles of circlesByMaterial.values()) {
    for (const circle of circles) {
      const look = looks.get(circle.material);
      if (!circle.decor || look.unit || isSpriteLook(look) || look.invisible) continue;
      ctx.beginPath();
      ctx.arc(circle.x, circle.y, circle.radius, 0, TAU);
      ctx.fillStyle = look.fill ?? look.base;
      ctx.fill();
      if (look.contour) {
        ctx.strokeStyle = look.contour.colour;
        ctx.lineWidth = look.contour.width;
        ctx.stroke();
      }
    }
  }

  // The walls' edges, before the piles: a tyre lying on a wall covers the edge under it, and that is the
  // one thing the edge must not do the other way round (see the note on `outlines`).
  ctx.lineJoin = 'round';
  ctx.lineCap = 'butt';
  for (const outline of outlines) {
    const look = looks.get(outline.surface);
    if (!look.contour) continue;
    // Dirt gathered at the foot of the wall, wider than the contour and under it: half of it falls on
    // the grass, which is what breaks the clean vector edge a drawn shape always has.
    if (look.dirt) {
      ctx.strokeStyle = look.dirt.colour;
      ctx.globalAlpha = look.dirt.alpha;
      ctx.lineWidth = look.contour.width * 3.4;
      strokeRun(ctx, outline.points);
      ctx.globalAlpha = 1;
    }
    ctx.strokeStyle = look.contour.colour;
    ctx.lineWidth = look.contour.width;
    strokeRun(ctx, outline.points);
  }

  for (const pile of piles) {
    // No texture (still loading, or the file is missing): the barrier is at least still there.
    if (!pile.tyres) {
      if (pile.chain) paintRoadSurface(ctx, [pile.chain], pile.look.base);
      else {
        ctx.fillStyle = pile.look.base;
        ctx.beginPath();
        ctx.arc(pile.circle.x, pile.circle.y, pile.circle.radius, 0, TAU);
        ctx.fill();
      }
    }
  }
  // The piles' own shadows, after the walls and before the tyres: a tyre lying on a wall casts its shadow
  // ON the wall (drawn before the wall, that shadow was simply buried by it), and all of them together so
  // that one pile's shadow never lands on the tyres of the pile next to it.
  paintPileShadows(ctx, piles.filter((pile) => pile.tyres));
  paintPileTyres(ctx, piles.filter((pile) => pile.tyres));

  for (const group of groups) {
    if (group.look.strip !== 'fence') continue;
    for (const strip of group.strips) drawFence(ctx, strip.spine, strip.thickness, group.look.source, group.look.plinth);
  }

  for (const group of groups) {
    if (!isBlockLook(group.look)) continue;
    if (group.look.composite === 'stand') {
      // Each rectangle retains its own frame, even when adjacent stands share a shadow.
      for (const { obstacle } of group.strips) {
        ctx.save();
        ctx.translate(obstacle.x, obstacle.y);
        ctx.rotate(obstacle.angle ?? 0);
        drawStand(ctx, obstacle.width, obstacle.height, group.look.source, createCanvas, obstacle.id ?? 0);
        ctx.restore();
      }
    } else {
      for (const { obstacle } of group.strips) paintBlockRect(ctx, obstacle, group.look, group.variant);
    }
  }
}

/** One sprite in its own rectangle, with the same frame and cover-fit as the editor. */
function paintBlockRect(ctx, obstacle, look, variant) {
  const sprite = look.sprites?.[variant ?? 0] ?? look.sprites?.[0];
  const spec = look.variants?.[variant ?? 0] ?? look.variants?.[0];
  ctx.save();
  ctx.translate(obstacle.x, obstacle.y);
  ctx.rotate(obstacle.angle ?? 0);
  ctx.beginPath();
  ctx.rect(-obstacle.width / 2, -obstacle.height / 2, obstacle.width, obstacle.height);
  ctx.clip();
  if (look.base) {
    ctx.fillStyle = look.base;
    ctx.fillRect(-obstacle.width / 2, -obstacle.height / 2, obstacle.width, obstacle.height);
  }
  if (sprite && spec?.length > 0 && spec?.depth > 0) {
    const scale = Math.max(obstacle.width / spec.length, obstacle.height / spec.depth);
    const width = spec.length * scale, height = spec.depth * scale;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(sprite, -width / 2, -height / 2, width, height);
  }
  ctx.restore();
}

/**
 * The textures of every surface style, keyed by name: asphalt fill, edge line, pit line and run-off.
 * Adding a style means adding its name to SURFACE_STYLES and its own branch here — no piece, no
 * boundary and no geometry code knows about styles.
 *
 * The first style is built from the shared tile rng, in the order the painter has always drawn it
 * from, so a track whose pieces name no style comes out pixel for pixel as it did before.
 */
function makeSurfaces(ctx, createCanvas, seed, p, tileRng) {
  const surfaces = new Map();
  SURFACE_STYLES.forEach((name, index) => {
    const tiles = index === 0 ? tileRng : mulberry32(seed ^ (0x51ed270b * (index + 1)));
    const wear = index === 0 ? mulberry32(seed ^ 0x27d4eb2f) : mulberry32(seed ^ (0x27d4eb2f * (index + 1)));
    // The tiles are kept as well as the patterns: paintUnion builds a pattern per outline, shifted to
    // that outline's own phase (see patternAt), so the wear does not repeat in the same places.
    const lineTile = makeWornLineTile(createCanvas, wear, p.line);
    const pitLineTile = makeWornLineTile(createCanvas, wear, p.pitLine);
    const sandTile = makeSandTile(createCanvas, wear, p.sand ?? EARTH);
    surfaces.set(name, {
      asphalt: repeatingPattern(ctx, makeAsphaltTile(createCanvas, tiles, p)),
      line: repeatingPattern(ctx, lineTile),
      pitLine: repeatingPattern(ctx, pitLineTile),
      sand: repeatingPattern(ctx, sandTile),
      lineTile,
      pitLineTile,
      sandTile,
    });
  });
  return surfaces;
}

/** A small occupancy raster and summed-area table keep whole-canopy queries cheap on large maps.
 * Geometry is painted rather than inferred from ground colours, so even explicit grass areas and
 * hidden obstacles remain reserved. This canvas is independent of the surface's bake resolution. */
function vegetationFreeSpace(track, createCanvas) {
  const scale = Math.min(0.25, 1024 / Math.max(track.width, track.height));
  const width = Math.ceil(track.width * scale), height = Math.ceil(track.height * scale);
  const mask = createCanvas(width, height), ctx = mask.getContext('2d');
  ctx.scale(scale, scale);
  ctx.fillStyle = ctx.strokeStyle = '#fff';
  const polygon = (points) => {
    if (points.length < 3) return;
    ctx.beginPath(); ctx.moveTo(points[0].x, points[0].y);
    for (const point of points.slice(1)) ctx.lineTo(point.x, point.y);
    ctx.closePath(); ctx.fill();
  };
  const surfaces = new Map([[DEFAULT_SURFACE, { asphalt: '#fff' }]]);
  for (const piece of collectPieces(track, track.roadWidth ?? 180)) paintPieceAsphalt(ctx, piece, surfaces);
  for (const area of track.terrainAreas ?? []) polygon(area.points ?? []);
  for (const { obstacle, spine, thickness } of barrierStrips(track)) {
    if (obstacle.type === 'rect') {
      ctx.save(); ctx.translate(obstacle.x, obstacle.y); ctx.rotate(obstacle.angle ?? 0);
      ctx.fillRect(-obstacle.width / 2, -obstacle.height / 2, obstacle.width, obstacle.height);
      ctx.restore();
    } else {
      ctx.lineWidth = thickness; ctx.lineCap = 'round'; ctx.lineJoin = 'miter';
      ctx.miterLimit = BARRIER_MITER_LIMIT;
      strokeRun(ctx, spine);
    }
  }
  for (const circle of barrierCircles(track)) {
    ctx.beginPath(); ctx.arc(circle.x, circle.y, circle.radius, 0, TAU); ctx.fill();
  }
  const pb = track.pitBox;
  if (pb) ctx.fillRect(Math.min(pb.x1, pb.x2), Math.min(pb.y1, pb.y2), Math.abs(pb.x2 - pb.x1), Math.abs(pb.y2 - pb.y1));
  for (const dec of track.decorations ?? []) {
    ctx.beginPath(); ctx.arc(dec.x, dec.y, dec.radius ?? 20, 0, TAU); ctx.fill();
  }
  const pixels = ctx.getImageData(0, 0, width, height).data;
  const stride = width + 1, sums = new Uint32Array(stride * (height + 1));
  for (let y = 0; y < height; y++) {
    let row = 0;
    for (let x = 0; x < width; x++) {
      row += pixels[(y * width + x) * 4 + 3] > 0 ? 1 : 0;
      sums[(y + 1) * stride + x + 1] = sums[y * stride + x + 1] + row;
    }
  }
  return (x, y, reach) => {
    if (x - reach < 0 || y - reach < 0 || x + reach > track.width || y + reach > track.height) return false;
    const x1 = Math.floor((x - reach) * scale), y1 = Math.floor((y - reach) * scale);
    const x2 = Math.ceil((x + reach) * scale), y2 = Math.ceil((y + reach) * scale);
    return sums[y2 * stride + x2] - sums[y1 * stride + x2]
      - sums[y2 * stride + x1] + sums[y1 * stride + x1] === 0;
  };
}

/** Sparse jittered candidates, stable for a track id; no track data or collision bodies are added. */
export function vegetationPlacements(track, opts = {}) {
  if (!track || !(track.width > 0 && track.height > 0)) return [];
  const free = vegetationFreeSpace(track, opts.createCanvas ?? defaultCreateCanvas);
  const rng = mulberry32(hashString(String(track.id ?? 'track')) ^ 0x718bcd49);
  const spacing = 220, placed = [], cells = new Map();
  for (let row = 0; row < Math.ceil(track.height / spacing); row++) {
    for (let col = 0; col < Math.ceil(track.width / spacing); col++) {
      if (rng() > 0.72) continue;
      const x = (col + 0.1 + rng() * 0.8) * spacing;
      const y = (row + 0.1 + rng() * 0.8) * spacing;
      const kind = rng() < 0.65 ? 'tree' : 'bush';
      const size = kind === 'tree' ? 85 + rng() * 50 : 30 + rng() * 28;
      const variant = Math.floor(rng() * 5);
      // The rectangle encloses the canopy AND its small shadow, plus 24 world units of clearance.
      if (!free(x, y, size / 2 + 32)) continue;
      let crowded = false;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const other = cells.get(`${col + dx},${row + dy}`);
        if (other && Math.hypot(x - other.x, y - other.y) < (size + other.size) * 0.71 + 35) crowded = true;
      }
      if (crowded) continue;
      const plant = { x, y, size, kind, src: `vegetation/${kind}-${variant + 1}.png` };
      placed.push(plant); cells.set(`${col},${row}`, plant);
    }
  }
  return placed;
}

function paintVegetation(ctx, track, opts) {
  if (!VEGETATION_SPRITES.some(({ src }) => opts.images?.[src])) return;
  paintPlants(ctx, vegetationPlacements(track, opts), opts.images);
}

/**
 * Paints the whole track surface into `ctx`, in world coordinates (ctx is expected to already be
 * scaled/translated/cleared as the caller wants).
 *
 * WHAT is painted is the track's whole look: the ground, the road with its lines, the markings and the
 * barriers over them. What the caller does with the canvas is not this painter's business: the track editor
 * writes it out as the track's own picture (see `exportPicture`), which is then what the game shows.
 *
 * opts:
 *  - palette: partial override of PALETTE
 *  - createCanvas(w, h): offscreen canvas factory (defaults to document.createElement)
 *  - scale: pixels per world unit the CALLER has already put on `ctx` (see createTrackCanvas). It only
 *    matters for the tiles: they are made to measure, so they have to know the resolution they land on
 *  - images: already-decoded drawables for image-backed materials (see materials.js), keyed by the
 *    material's `image.src` — a material whose image isn't in here yet paints its flat base colour
 *    instead (see paintTyreChain), so a track never blocks on a load.
 *  - terrainImages: already-decoded GROUND textures (see terrainImages below), keyed by terrain name
 *    — the ground the base surface is painted with (see grassTextureOf) plus one entry per terrain
 *    area actually drawn. A terrain whose image isn't there is painted from its own procedural look
 *    instead (see paintTerrains), so a track never blocks on a load and a missing file is a plainer
 *    track, never a broken one.
 */
export function paintTrack(ctx, track, opts = {}) {
  if (!track || !(track.width > 0) || !(track.height > 0)) return;
  const p = { ...PALETTE, ...(opts.palette ?? {}) };
  const createCanvas = resoledCanvas(opts.createCanvas ?? defaultCreateCanvas, opts.scale ?? 1);
  const seed = hashString(String(track.id ?? 'track'));
  const terrainImages = opts.terrainImages ?? {};

  const tileRng = mulberry32(seed ^ 0x9e3779b9);
  const decorRng = mulberry32(seed ^ 0x85ebca6b);
  // The grass has its own seed so inside and outside stay identical regardless of road materials. The
  // picture of the base surface is filed under the ground it is made of, so a terrain area of that same
  // ground shares it and one of another ground keeps its own (see terrainImages).
  const surfaces = makeSurfaces(ctx, createCanvas, seed, p, tileRng);
  const baseImage = terrainImages[grassTextureOf(track)];
  const grassPattern = repeatingPattern(ctx, makeGrassTile(createCanvas, mulberry32(seed ^ 0x57a551ed), p, baseImage));

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  paintGrass(ctx, track, decorRng, p, grassPattern, !!baseImage);

  // Ground over the grass, under the road (see paintTerrains: terrain is the surface the asphalt
  // sits on, so it must be painted before the asphalt and its lines).
  const bounds = opts.bounds ?? { x: 0, y: 0, width: track.width, height: track.height };
  paintTerrains(ctx, track, createCanvas, seed, terrainImages, bounds);

  const roadWidth = track.roadWidth ?? 180;
  const pieces = collectPieces(track, roadWidth);
  for (const piece of pieces) paintPieceAsphalt(ctx, piece, surfaces);
  paintUnion(ctx, pieces, mulberry32(seed ^ 0x1b873593), p, surfaces);

  const startLine = (track.checkpoints ?? [])[0];
  if (startLine) paintStartBand(ctx, startLine, pieces, roadWidth, p);
  if (track.pitBox) paintPitBox(ctx, track.pitBox, p);
  paintVegetation(ctx, track, opts);

  // Objects last: a barrier stands ON the surface, so its shadow falls over whatever is under it.
  paintBarriers(ctx, track, createCanvas, seed, opts);

  ctx.restore();
}

/**
 * Paints a track into a fresh offscreen canvas sized to the track (the whole world), which is what
 * both the game (as a texture) and the editor preview (as the static base layer) actually want.
 *
 * `opts.scale` is the resolution it is baked at, in pixels per world unit: 1 is one for one, and the
 * default comes from paintScaleFor (see there for why more than one is worth its memory). A caller can
 * still ask for a particular one — a tool that wants the smallest possible canvas says 1.
 */
export function createTrackCanvas(track, opts = {}) {
  const scale = opts.scale ?? paintScaleFor(track);
  const createCanvas = opts.createCanvas ?? defaultCreateCanvas;
  const canvas = createCanvas(
    Math.max(1, Math.round((track?.width ?? 1) * scale)),
    Math.max(1, Math.round((track?.height ?? 1) * scale)),
  );
  const ctx = canvas.getContext('2d');
  if (scale !== 1) ctx.scale(scale, scale);
  paintTrack(ctx, track, { ...opts, scale });
  return canvas;
}

/**
 * Pixels per world unit the surface is baked at.
 *
 * The surface is the layer everything else is drawn on top of, and it is baked once, at a fixed
 * resolution: baked one for one a tyre is ten pixels — as wide as it is in the world — and the camera
 * (0.4x to 2x) is then free to magnify them, which is what made the piles in game look softer than the
 * same pile in the editor, where they are stamped fresh out of the sprite at every zoom. Two bakes the
 * piles at the size the sprite still has the detail for, and halves the work the display's own filter
 * has to do at any zoom in.
 *
 * It costs four times the memory (a 2832x3366 track: 38 MB baked one for one, 152 MB at two) and four
 * times the painting time on load, which is why the two ceilings are here rather than left to whoever
 * asks: a big map settles at whatever it can carry, and never goes below one.
 */
/** The scale to bake `track` at in the game: `wanted`, if it stays within the ceilings, and never under 1. */
export function paintScaleFor(track, wanted = PAINT_SCALE) {
  return Math.max(1, scaleWithinCeilings(track, wanted));
}

/**
 * Everything the painter reads OF THE TRACK ITSELF: the geometry (pieces with their curves and widths, the
 * markings, the terrain figures, the barriers, the pit box, the reserved space) and the two switches that
 * say what a picture contains. Two tracks with this value are painted into the same picture.
 *
 * Outside it on purpose: the painter's own version, and the resolution a picture is baked at — a picture is
 * a frozen look, and the same geometry painted at another size is still the same picture.
 */
export function trackShapeKey(track) {
  const parts = [track?.id ?? '', track?.width ?? 0, track?.height ?? 0, track?.roadWidth ?? 0];
  // The ground the base surface is painted with is part of the picture like everything else below.
  parts.push('grass', track?.grassTexture ?? '');
  // Everything the painter reads: the pieces (curves, handles and widths included) and the markings.
  // Two exports that differ anywhere here get different keys, so a picture can never outlive the geometry
  // it was drawn from.
  for (const piece of track?.pieces ?? []) {
    parts.push(piece.id ?? '', piece.kind ?? '', piece.lane ?? '', piece.surface ?? '', piece.closed ? 1 : 0, piece.pit ? 1 : 0, piece.width ?? 0);
    for (const node of piece.nodes ?? []) {
      parts.push(Math.round(node.x ?? 0), Math.round(node.y ?? 0), node.w ?? 0);
      if (node.hIn) parts.push(Math.round(node.hIn.x), Math.round(node.hIn.y));
      if (node.hOut) parts.push(Math.round(node.hOut.x), Math.round(node.hOut.y));
    }
  }
  for (const cp of track?.checkpoints ?? []) {
    parts.push(cp.x1 | 0, cp.y1 | 0, cp.x2 | 0, cp.y2 | 0);
  }
  // The terrain areas as well: they are painted into the same picture, so a track that gains one (or
  // changes its terrain, whether it blends, or its layer) gets a different key.
  for (const area of track?.terrainAreas ?? []) {
    parts.push(terrainOf(area));
    parts.push(area.blend ?? '');
    if (area.z != null) parts.push('z', area.z);
    for (const point of area.points ?? []) parts.push(point.x | 0, point.y | 0);
  }
  // The barriers as well: they are painted into the same picture, so a track that gains a wall gets a
  // different key (see paintBarriers).
  for (const obstacle of track?.obstacles ?? []) {
    parts.push(obstacle.type ?? '', obstacle.material ?? '', obstacle.thickness ?? 0, obstacle.radius ?? 0);
    if (obstacle.z != null) parts.push('z', obstacle.z);
    // A sprite material's picture changes with its variant, so the variant is part of the picture too.
    if (obstacle.variant != null) parts.push(obstacle.variant);
    if (obstacle.type === 'wall') parts.push(obstacle.x1 | 0, obstacle.y1 | 0, obstacle.x2 | 0, obstacle.y2 | 0);
    else if (obstacle.type === 'rect') parts.push(obstacle.x | 0, obstacle.y | 0, obstacle.width | 0, obstacle.height | 0, obstacle.angle ?? 0);
    else if (obstacle.type === 'circle') parts.push(obstacle.x | 0, obstacle.y | 0);
    else for (const point of obstacle.points ?? obstacle.nodes ?? []) parts.push(point.x | 0, point.y | 0);
  }
  const pb = track?.pitBox;
  if (pb) parts.push(pb.x1 | 0, pb.y1 | 0, pb.x2 | 0, pb.y2 | 0);
  // Existing decorative trees reserve space in the scatter as well.
  parts.push('vegetation-reservations', JSON.stringify(track?.decorations ?? []));
  return hashString(parts.join(','));
}

/**
 * Cache key for one track's painted texture: the shape above, the painter's own version and the resolution.
 * All three are part of what a texture IS, so none of them may hand back a texture baked for another.
 */
export function trackPaintKey(track, scale = paintScaleFor(track)) {
  return `track_paint_${hashString([PAINTER_VERSION, trackShapeKey(track), `scale-${scale.toFixed(3)}`].join(','))}`;
}

/**
 * The value an exported track file carries as `backgroundStamp`: everything that decides the bytes
 * of the picture AND of the tile pyramid. It is what the game appends to the URLs, so a change to
 * the painter itself (no geometry moved) has to change it too — otherwise a browser that already
 * has the old tiles would keep drawing them.
 */
export function trackExportStamp(track) {
  return hashString([PAINTER_VERSION, trackShapeKey(track), 'tiles-gutter-1', 'base-0.25'].join(','));
}

/**
 * The image-backed obstacle materials and automatic vegetation, as `{ name, src }`. The painter can't
 * fetch them (no DOM), so the host loads each `src` (resolved against its own asset root) and hands
 * the decoded files back in `opts.images` — see paintTrack.
 *
 * A sprite material asks for the ONE variant it uses, so a track that never places a variant never
 * downloads it.
 */
export function materialImages(track) {
  const seen = new Set();
  const out = [];
  for (const obstacle of track?.obstacles ?? []) {
    const look = materialLook(track, materialNameFor(track, obstacle));
    if (look.invisible) continue;
    const variant = lookVariant(look, obstacle);
    const src = look.composite ? (obstacle.type === 'rect' ? look.image?.src : null)
      : variant === null ? look.image?.src : look.variants[variant].src;
    if (!src || seen.has(src)) continue;
    seen.add(src);
    out.push({ name: materialNameFor(track, obstacle), src });
  }
  if (track?.width > 0 && track?.height > 0) {
    for (const { src } of VEGETATION_SPRITES) out.push({ name: 'vegetation', src });
  }
  return out;
}

/**
 * The GROUND textures a track actually needs, as `{ terrain, src }`: the ground the base surface is
 * painted with (see grassTextureOf) plus one per terrain area drawn. A name is its own picture, so a
 * figure of the same ground as the base shares its file, while a figure of another one (`grass` over a
 * lawn) gets its own. The painter can't fetch them (no DOM), so the host loads each `src` (found by the
 * `<terrain>.jpg` convention, resolved against its own asset root like materialImages) and hands the
 * decoded files back in paintTrack's `opts.terrainImages`, keyed by terrain name.
 */
export function terrainImages(track) {
  const grassTexture = grassTextureOf(track);
  const out = new Map([[grassTexture, terrainImageSrc(grassTexture)]]);
  for (const area of track?.terrainAreas ?? []) {
    const name = terrainOf(area);
    if (!out.has(name)) out.set(name, terrainImageSrc(name));
  }
  return [...out].map(([terrain, src]) => ({ terrain, src }));
}
